import React, { useState, useEffect, useMemo, useRef, createContext, useContext } from 'react';
import { onAuthStateChanged, signInWithPopup, signInWithCredential, signOut, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, sendEmailVerification, updateProfile, User, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, collection, onSnapshot, query, where, addDoc, deleteDoc, getDocs, limit, deleteField, writeBatch } from 'firebase/firestore';
import { aggregatePlayerStats } from './lib/import/gc-save';
import { detectSignupOverlap, statLinesWithoutSignup } from './lib/signup-overlap';
import { auth, db, googleProvider, handleFirestoreError, OperationType, storage } from './firebase';
import { Toaster, showSaveError } from './components/Toast';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';

// Registered once at module load (not per click) so Capacitor doesn't warn
// "plugin already registered". Returns a proxy that bridges to the native
// GoogleSignInPlugin; harmless on web since we gate calls on isNativePlatform().
const GoogleSignInPlugin = registerPlugin<{
  signIn(): Promise<{ idToken: string; accessToken: string }>;
}>('GoogleSignInPlugin');
// Native Sign in with Apple (App Store requires it alongside Google). The plugin
// returns the raw nonce so Firebase can verify the credential. Gated on
// isNativePlatform() at call sites; harmless on web.
const AppleSignInPlugin = registerPlugin<{
  signIn(): Promise<{ idToken: string; rawNonce: string; givenName: string; familyName: string }>;
}>('AppleSignInPlugin');
import { cn, calculateOVR, blendScoutingOVR, normalizeAttributes, deepClean, currentSeasonGames } from './lib/utils';
import { startDemoSession, getDemoTimeRemaining, cleanupExpiredDemoSession } from './lib/demo-session';
import { drainSharedFiles } from './lib/share-import';
import { calculateXOVR } from './lib/xovr-engine';
import { generateAutoLineup, isEligible } from './lib/lineupUtils';
import { battingStyleHasEH, battingOrderSize } from './lib/lineup-engine';
import { generateOptimalLineup } from './lib/lineup-engine';
import { aggregateBatterSignals, BatterSeasonSignal } from './lib/insights/season-signals';
import { PITCH_COUNT_RULESETS, getPitcherEligibility, buildCustomTournamentRules } from './lib/pitch-count-rules';
import { XOVRLabel } from './components/XOVRLabel';
import { 
  Trophy, 
  Users, 
  Calendar, 
  Settings, 
  Plus, 
  LogOut, 
  ChevronRight,
  Shield,
  Activity,
  Search,
  Upload,
  BarChart3,
  Play,
  PlayCircle,
  ClipboardList,
  UserPlus,
  FileUp,
  X,
  MapPin,
  Clock,
  Trash2,
  UserMinus,
  Sparkles,
  Check,
  Layout,
  LayoutDashboard,
  GripVertical,
  Pencil,
  AlertTriangle,
  Info,
  ChevronDown,
  Target,
  Lock,
  User as UserIcon
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { format, parseISO } from 'date-fns';
import { motion, AnimatePresence, Reorder } from 'motion/react';
import Markdown from 'react-markdown';
import Papa from 'papaparse';

import { SettingsView } from './components/SettingsView';
import { CalendarImporter } from './components/CalendarImporter';
import { RosterScoutingSession } from './components/RosterScoutingSession';
import { ScoutingElitePrompt } from './components/ScoutingElitePrompt';
import { ChecklistCalendarSync } from './components/ChecklistCalendarSync';
import { StatsView } from './components/StatsView';
import { SeasonHistoryModal } from './components/SeasonHistoryModal';
import { SeasonWrappedModal } from './components/SeasonWrappedModal';
import { PlanGameView } from './components/PlanGameView';
import { GamePositionsEditor } from './components/GamePositionsEditor';
import { GameDataImport } from './components/Import/GameDataImport';
import { isGameLive } from './lib/game-utils';
import { HomeTab } from './components/home/HomeTab';
import { LiveGameEngine } from './components/LiveGameEngine';
import { TournamentForm } from './components/TournamentForm';
import { TournamentView } from './components/TournamentView';
import { TournamentGameStrategy } from './components/TournamentGameStrategy';
import { StatsImportModal } from './components/StatsImportModal';
import { GameInsightCard, groupInsightsByInning, InsightCardData } from './components/Insights/GameInsightCard';
import { GameInsightsSheet } from './components/Insights/GameInsightsSheet';
import { EnterResultsModal } from './components/EnterResultsModal';
import { OnboardingWizard } from './components/OnboardingWizard';
import { RotatingStatus } from './components/RotatingStatus';
import { SyncNote } from './components/SyncNote';
import { FoundingCoachForm } from './components/FoundingCoachForm';
import { XOVRBreakdown as XOVRBreakdownCard } from './components/XOVRBreakdown';
import { XOVRTrendChart } from './components/XOVRTrendChart';
import { UserAccountMenu } from './components/UserAccountMenu';
import { ManageAccountModal } from './components/ManageAccountModal';
import { ScoutingWizard } from './components/ScoutingWizard';
import { EliteUpgradeModal } from './components/EliteUpgradeModal';
import { useSubscription } from './hooks/useSubscription';
import { track, suppressAnalytics, resolvePendingCheckout, setAnalyticsUser } from './lib/analytics';
import { SplashScreen } from '@capacitor/splash-screen';
import { computePlayerHotCold } from './lib/streaks';
import { TermsOfService, PrivacyPolicy } from './components/LegalModals';
import { LandingPage } from './components/LandingPage';
import { NativeWelcome } from './components/NativeWelcome';
import { PlayerDevReviewModal } from './components/PlayerDevReviewModal';
import { computeDevReviewProposals } from './lib/dev-review';
import { reconcileGameReminders } from './lib/game-reminders';
import { initPushRegistration, clearPushRegistration, addScoutNudgeTapListener, reconcileScoutNudgeTimes } from './lib/push-notifications';
import { setHapticsEnabled, tapLight, tapMedium, celebrate } from './lib/haptics';
import { PlayerForm } from './components/PlayerForm';
import { RosterTab } from './components/RosterTab';
import { LineupBuilder } from './components/LineupBuilder';
import { GamesTab } from './components/games/GamesTab';

// --- Contexts ---
const AuthContext = createContext<{
  user: User | null;
  loading: boolean;
  coachData: any;
}>({ user: null, loading: true, coachData: null });

// --- Components ---

// ── Bottom nav icons — outline when inactive, filled when active ──────────────
const NavHomePlate = ({ active, className }: { active: boolean; className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" className={className}>
    {active
      ? <path d="M4 4h16v10l-8 6-8-6z" fill="currentColor" stroke="none" />
      : <path d="M4 4h16v10l-8 6-8-6z" stroke="currentColor" strokeWidth="1.75" />}
  </svg>
);

const NavRoster = ({ active, className }: { active: boolean; className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" className={className}>
    {active ? (
      <>
        <circle cx="9" cy="7" r="3.5" fill="currentColor" />
        <path d="M2 20c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="currentColor" strokeWidth="2" fill="none" />
        <circle cx="17" cy="8" r="2.5" fill="currentColor" />
        <path d="M20 20c0-2.5-1.5-4.5-4-5.5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
      </>
    ) : (
      <>
        <circle cx="9" cy="7" r="3.5" stroke="currentColor" strokeWidth="1.75" />
        <path d="M2 20c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="currentColor" strokeWidth="1.75" />
        <circle cx="17" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.75" />
        <path d="M20 20c0-2.5-1.5-4.5-4-5.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      </>
    )}
  </svg>
);

const NavCalendar = ({ active, className }: { active: boolean; className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" className={className}>
    {active ? (
      <>
        <rect x="3" y="4" width="18" height="17" rx="2" fill="currentColor" />
        <path d="M3 9h18" stroke="white" strokeWidth="1.5" />
        <path d="M8 2v3M16 2v3" stroke="currentColor" strokeWidth="1.75" />
        <rect x="7" y="13" width="3" height="3" rx="0.5" fill="white" />
        <rect x="14" y="13" width="3" height="3" rx="0.5" fill="white" />
      </>
    ) : (
      <>
        <rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="1.75" />
        <path d="M3 9h18" stroke="currentColor" strokeWidth="1.75" />
        <path d="M8 2v3M16 2v3" stroke="currentColor" strokeWidth="1.75" />
        <rect x="7" y="13" width="3" height="3" rx="0.5" fill="currentColor" />
        <rect x="14" y="13" width="3" height="3" rx="0.5" fill="currentColor" />
      </>
    )}
  </svg>
);

const NavStats = ({ active, className }: { active: boolean; className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" className={className}>
    {active ? (
      <>
        <rect x="3" y="12" width="4" height="9" rx="1" fill="currentColor" />
        <rect x="10" y="7" width="4" height="14" rx="1" fill="currentColor" />
        <rect x="17" y="3" width="4" height="18" rx="1" fill="currentColor" />
      </>
    ) : (
      <>
        <rect x="3" y="12" width="4" height="9" rx="1" stroke="currentColor" strokeWidth="1.75" />
        <rect x="10" y="7" width="4" height="14" rx="1" stroke="currentColor" strokeWidth="1.75" />
        <rect x="17" y="3" width="4" height="18" rx="1" stroke="currentColor" strokeWidth="1.75" />
      </>
    )}
  </svg>
);

const NavSettings = ({ active, className }: { active: boolean; className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" className={className}>
    {active ? (
      <>
        <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" fill="currentColor" />
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" fill="currentColor" stroke="none" />
      </>
    ) : (
      <>
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.75" />
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="1.75" />
      </>
    )}
  </svg>
);

// Full-screen crash fallback shared by both error paths below.
const CrashScreen = ({ title, message }: { title: string; message: string }) => (
  <div className="min-h-screen bg-surface flex items-center justify-center p-6 text-ink text-center">
    <div className="max-w-md space-y-4">
      <Shield className="w-16 h-16 text-danger mx-auto" />
      <h1 className="text-2xl font-bold">{title}</h1>
      <p className="text-ink-2">{message}</p>
      <button
        onClick={() => window.location.reload()}
        className="px-6 py-2 bg-ink text-surface rounded-full font-bold"
      >
        Retry
      </button>
    </div>
  </div>
);

// Real React error boundary (class — function components can't catch render
// errors). A crash anywhere in the tree shows a retry screen instead of a
// blank page. Sentry already receives the error via the root onCaughtError
// handler in main.tsx, so no capture call is needed here.
class RenderErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <CrashScreen
          title="We took a bad hop"
          message="Something broke on our side. Your data is safe. Hit retry to get back in the game."
        />
      );
    }
    return this.props.children;
  }
}

const ErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const [errorInfo, setErrorInfo] = useState<any>(null);

  useEffect(() => {
    // handleFirestoreError (src/firebase.ts) throws an Error whose message is
    // a JSON payload. Depending on the call site it surfaces as a window
    // "error" (sync) or an "unhandledrejection" (async) — listen for both.
    const inspect = (err: unknown) => {
      if (!(err instanceof Error)) return;
      try {
        setErrorInfo(JSON.parse(err.message));
      } catch {
        // Not a firestore error we care about here
      }
    };
    const handleError = (event: ErrorEvent) => inspect(event.error);
    const handleRejection = (event: PromiseRejectionEvent) => inspect(event.reason);
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  if (errorInfo) {
    return (
      <CrashScreen
        title="Access Restricted"
        message={errorInfo?.error || 'Something went wrong with the dugout data.'}
      />
    );
  }

  return <RenderErrorBoundary>{children}</RenderErrorBoundary>;
};

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [coachData, setCoachData] = useState<any>(null);

  useEffect(() => {
    // Live listener on the signed-in coach's users/{uid} doc. Kept in a ref so
    // it survives across auth-state callbacks and is torn down on sign-out. This
    // is what makes a just-saved profile photo (written to Firestore) appear on
    // every avatar immediately, instead of only after an app restart.
    let unsubUserDoc: (() => void) | null = null;
    const stopUserDoc = () => { if (unsubUserDoc) { unsubUserDoc(); unsubUserDoc = null; } };

    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      stopUserDoc();
      // Anonymous = a demo session (startDemoSession signs in anonymously).
      // Demo sessions must NOT bootstrap a users/{uid} profile. cleanupDemoSessions
      // deletes the demo team and the anonymous Auth user, but never the Firestore
      // doc — so before this guard every demo left behind a permanent null-email
      // "coach" and inflated every coach count in the nightly rollup.
      // coachData simply stays null for a demo, which is already a supported state:
      // the catch below leaves it null too, and every consumer falls back to `user`.
      if (u && !u.isAnonymous) {
        try {
          const userDoc = await getDoc(doc(db, 'users', u.uid));
          if (userDoc.exists()) {
            const existingData = userDoc.data();
            // A users/{uid} doc can EXIST without ever having had a profile written
            // to it: push registration writes `fcmTokens` with setDoc(merge)
            // (src/lib/push-notifications.ts), and setDoc-merge CREATES the doc when
            // the APNs token beats this bootstrap. That doc has no email, and before
            // this backfill the branch below only ever synced photoURL — so the email
            // was never written, permanently. On iOS that cost the coach their Season
            // Pass and dropped them out of every coach metric, which now filters on
            // email. Backfill whatever is missing rather than assuming "exists" means
            // "complete".
            const patch: Record<string, any> = {};
            if (!existingData.uid) patch.uid = u.uid;
            if (!existingData.email && u.email) patch.email = u.email;
            if (!existingData.displayName && u.displayName) patch.displayName = u.displayName;
            if (!existingData.role) patch.role = 'coach';
            if (!existingData.createdAt) patch.createdAt = new Date().toISOString();
            // Sync Google Photo if it changed or was missing
            if (u.photoURL && existingData.photoURL !== u.photoURL) patch.photoURL = u.photoURL;

            if (Object.keys(patch).length > 0) {
              await setDoc(doc(db, 'users', u.uid), patch, { merge: true });
              setCoachData({ ...existingData, ...patch });
            } else {
              setCoachData(existingData);
            }
          } else {
            const newData = {
              uid: u.uid,
              email: u.email,
              displayName: u.displayName,
              photoURL: u.photoURL,
              role: 'coach',
              createdAt: new Date().toISOString()
            };
            await setDoc(doc(db, 'users', u.uid), newData);
            setCoachData(newData);
          }
          // Now keep coachData live so profile edits (photo, name) reflect
          // everywhere the moment they're written to Firestore.
          unsubUserDoc = onSnapshot(doc(db, 'users', u.uid), (snap) => {
            if (snap.exists()) setCoachData(snap.data());
          });
        } catch (err) {
          console.warn('Could not read/write user profile — continuing anyway:', err);
        }
        // Post-game Scout nudges: save this device's push token under the
        // signed-in coach. Silent no-op on web and until the notification
        // permission has been granted (the pre-game reminders flow owns that
        // prompt). Demo sessions never reach here — the guard above excludes them.
        void initPushRegistration(u.uid);
      }
      setLoading(false);
      // Auth + profile resolved → React is ready to paint. Hide the native
      // splash now so there's no white flash between launch screen and app.
      // No-op on web. Catch is intentional — never block on a cosmetic hide.
      SplashScreen.hide().catch(() => {});
    });
    return () => { unsubAuth(); stopUserDoc(); };
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, coachData }}>
      {children}
    </AuthContext.Provider>
  );
};

// Shown while the coach's teams resolve. Normally a blink; with no signal and
// nothing cached on this phone it holds (see the team reconcile in Dashboard),
// so after a few seconds say why instead of spinning silently.
const TeamLoadingScreen = () => {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 6000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-canvas px-8 text-center">
      <Trophy className="w-10 h-10 text-accent-ink animate-spin" style={{ animationDuration: '1.6s' }} />
      {slow && (
        <p className="mt-6 text-sm font-semibold text-ink-2 max-w-xs">
          Waiting for signal to load your team. Once it loads, it stays on this phone for fields with no service.
        </p>
      )}
    </div>
  );
};

// Detects when Dugout is running inside an in-app browser (the mini-browser
// that opens links inside Instagram, Facebook, TikTok, etc.) rather than real
// Safari/Chrome. Google OAuth popups are blocked in these contexts, so we show
// the coach a tip to open in their real browser. Email sign-in still works.
const isInAppBrowser = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // Known in-app browser user-agent tokens.
  const tokens = [
    'FBAN', 'FBAV', 'FB_IAB',        // Facebook / Messenger
    'Instagram',                      // Instagram
    'Line/',                          // LINE
    'Twitter', 'TwitterAndroid',      // X / Twitter
    'Snapchat',                       // Snapchat
    'TikTok', 'BytedanceWebview',     // TikTok
    'Pinterest',                      // Pinterest
    'GSA/',                           // Google App in-app browser
  ];
  if (tokens.some((t) => ua.includes(t))) return true;
  // iOS in-app browsers inject custom webkit message handlers that real
  // Safari does not expose — the navigationPerformanceLogger one showed up
  // in our Sentry logs. Its presence is a strong in-app-browser signal.
  try {
    const handlers = (window as any)?.webkit?.messageHandlers;
    if (handlers && handlers.navigationPerformanceLoggerWithReply) return true;
  } catch {
    /* accessing webkit internals can throw — ignore */
  }
  return false;
};

const Login = ({ onDemoLoadingChange }: { onDemoLoadingChange?: (v: boolean) => void }) => {
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  // Deep-link the legal modals via URL hash so #privacy / #terms are real,
  // paste-anywhere links (App Store / Stripe / email footers need a URL that
  // loads the policy on a cold visit, not a click-to-open modal). Runs on the
  // logged-out landing surface, which is exactly where a fresh visitor lands.
  useEffect(() => {
    const openFromHash = () => {
      const h = window.location.hash.toLowerCase();
      if (h.includes('privacy')) setShowPrivacy(true);
      else if (h.includes('terms')) setShowTerms(true);
    };
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
    return () => window.removeEventListener('hashchange', openFromHash);
  }, []);
  const [emailMode, setEmailMode] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  // Focused sign-in modal for returning coaches — opened by the top-right
  // "Sign In" nav button. Shares the same auth handlers/state as the hero; this
  // is purely a different presentation surface, not a different auth path.
  const [signInOpen, setSignInOpen] = useState(false);
  // Signup ("Create Account") overlay state — lifted here (rather than living
  // inside LandingPage) so the native NativeWelcome screen can open it too.
  const [signupOpen, setSignupOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Coach name — captured on the signup overlay so email coaches arrive named,
  // matching what Google sign-in already provides via displayName.
  const [signupName, setSignupName] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState('');
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  // Computed once on mount — true when opened inside a social-app in-app browser.
  const [inAppBrowser] = useState(isInAppBrowser);

  const handleTryDemo = async () => {
    setDemoLoading(true);
    setDemoError('');
    onDemoLoadingChange?.(true);
    try {
      await startDemoSession();
    } catch (err: any) {
      console.error('Demo session failed', err);
      setDemoError('Could not start demo. Please try again.');
    } finally {
      setDemoLoading(false);
      onDemoLoadingChange?.(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        const result = await GoogleSignInPlugin.signIn();
        const credential = GoogleAuthProvider.credential(result.idToken);
        await signInWithCredential(auth, credential);
      } else {
        await signInWithPopup(auth, googleProvider);
      }
    } catch (error: any) {
      console.error("Login failed", JSON.stringify(error), error?.message, error?.code);
    }
  };

  // Sign in with Apple, on both native and web — critical so a coach who signed
  // up with Apple on iOS isn't trapped there (they can also log into the website).
  // Native: the plugin runs the ASAuthorization flow and returns Apple's identity
  // token + raw nonce, which Firebase verifies via OAuthProvider. Apple only sends
  // the name on the first sign-in, so we set displayName when present and missing.
  // Web: Firebase's signInWithPopup handles the OAuth round-trip (through the Apple
  // Services ID return URL); no plugin or manual nonce needed.
  const handleAppleLogin = async () => {
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('name');
      provider.addScope('email');
      if (Capacitor.isNativePlatform()) {
        const result = await AppleSignInPlugin.signIn();
        const credential = provider.credential({
          idToken: result.idToken,
          rawNonce: result.rawNonce,
        });
        const cred = await signInWithCredential(auth, credential);
        const fullName = `${result.givenName} ${result.familyName}`.trim();
        if (fullName && !cred.user.displayName) {
          try { await updateProfile(cred.user, { displayName: fullName }); } catch { /* non-fatal */ }
        }
      } else {
        await signInWithPopup(auth, provider);
      }
    } catch (error: any) {
      // "canceled" is the user backing out — not worth logging as a failure.
      if (error?.message !== 'canceled') {
        console.error("Apple login failed", JSON.stringify(error), error?.message, error?.code);
      }
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    try {
      if (authMode === 'signin') {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        // Save the coach's name to the Firebase user so it flows through the
        // same displayName path Google sign-in uses. Guarded: a name-write
        // hiccup must never block an otherwise-successful account creation.
        const name = signupName.trim();
        if (name) {
          try { await updateProfile(cred.user, { displayName: name }); } catch { /* non-fatal */ }
        }
        await sendEmailVerification(cred.user);
        setAwaitingVerification(true);
        setResendCooldown(60);
      }
    } catch (err: any) {
      const code = err?.code ?? '';
      if (code === 'auth/user-not-found' || code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setAuthError('Incorrect email or password.');
      } else if (code === 'auth/email-already-in-use') {
        setAuthError('An account with that email already exists. Try signing in instead.');
      } else if (code === 'auth/weak-password') {
        setAuthError('Password must be at least 6 characters.');
      } else if (code === 'auth/invalid-email') {
        setAuthError('Please enter a valid email address.');
      } else {
        setAuthError('Something went wrong. Please try again.');
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!auth.currentUser) return;
    try {
      await sendEmailVerification(auth.currentUser);
      setResendCooldown(60);
    } catch {
      // silently ignore — Firebase rate-limits this on its own
    }
  };

  const handleWrongEmail = async () => {
    await signOut(auth);
    setAwaitingVerification(false);
    setEmail('');
    setPassword('');
    setAuthMode('signup');
    setEmailMode(true);
  };

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (!awaitingVerification) return;
    const interval = setInterval(async () => {
      if (!auth.currentUser) return;
      await auth.currentUser.reload();
      if (auth.currentUser.emailVerified) {
        setAwaitingVerification(false);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [awaitingVerification]);

  const handlePasswordReset = async () => {
    if (!email) { setAuthError('Enter your email address above first.'); return; }
    setAuthError('');
    setAuthLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch {
      setAuthError('Could not send reset email. Check the address and try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  if (awaitingVerification) return (
    <div className="min-h-screen bg-surface flex flex-col items-center justify-center px-6 text-center">
      <div className="w-16 h-16 rounded-full bg-canvas border border-hairline flex items-center justify-center mb-6">
        <svg className="w-8 h-8 text-accent-ink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
      <h2 className="text-2xl font-black text-ink mb-2">Check your email</h2>
      <p className="text-ink-2 text-sm mb-1">We sent a verification link to</p>
      <p className="text-ink font-semibold text-sm mb-6">{email}</p>
      <p className="text-ink-2 text-xs mb-8 max-w-xs">Click the link in that email to confirm your address, then you'll be taken straight into Dugout.</p>
      <button
        onClick={handleResendVerification}
        disabled={resendCooldown > 0}
        className="w-full max-w-xs py-3 rounded-xl bg-raised border border-ink-mute text-ink text-sm font-semibold disabled:opacity-40 mb-3"
      >
        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend verification email'}
      </button>
      <button
        onClick={handleWrongEmail}
        className="text-ink-2 text-xs underline underline-offset-2"
      >
        Wrong email address? Start over
      </button>
    </div>
  );

  return (
    <>
      {/* Native app: a coach here already downloaded from the App Store, so the
          web marketing funnel is the wrong front door. Show a brief branded
          welcome that goes straight to auth. LandingPage stays mounted beneath
          it (its overlays own the actual sign-in/signup forms), and the welcome
          drives the SAME lifted auth state — so there's one auth path. The
          welcome hides itself whenever an auth overlay or the demo is open. */}
      {Capacitor.isNativePlatform() && !signInOpen && !signupOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50 }}>
          <NativeWelcome
            onGetStarted={() => { setAuthMode('signup'); setAuthError(''); setSignupOpen(true); }}
            onSignIn={() => { setAuthMode('signin'); setAuthError(''); setSignInOpen(true); }}
            onTryDemo={handleTryDemo}
            demoLoading={demoLoading}
            demoError={demoError}
          />
        </div>
      )}
      <LandingPage
        onGoogleLogin={handleGoogleLogin}
        onAppleLogin={handleAppleLogin}
        onTryDemo={handleTryDemo}
        demoVideoUrl="/dugout-demo.mp4"
        demoLoading={demoLoading}
        demoError={demoError}
        inAppBrowser={inAppBrowser}
        emailMode={emailMode}
        setEmailMode={setEmailMode}
        authMode={authMode}
        setAuthMode={setAuthMode}
        signInOpen={signInOpen}
        setSignInOpen={setSignInOpen}
        signupOpen={signupOpen}
        setSignupOpen={setSignupOpen}
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        signupName={signupName}
        setSignupName={setSignupName}
        authError={authError}
        setAuthError={setAuthError}
        authLoading={authLoading}
        resetSent={resetSent}
        showReset={showReset}
        setShowReset={setShowReset}
        onEmailSubmit={handleEmailSubmit}
        onPasswordReset={handlePasswordReset}
        onShowTerms={() => setShowTerms(true)}
        onShowPrivacy={() => setShowPrivacy(true)}
      />
      {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
    </>
  );
};


const GameForm = ({
  teamId,
  team,
  game,
  onClose,
  onGameSaved,
}: {
  teamId: string;
  team?: any;
  game?: any;
  onClose: () => void;
  // Optimistic hand-off so a newly scheduled game appears immediately, before
  // the (native-lagged) Firestore snapshot catches up. Deduped by id upstream.
  onGameSaved?: (g: any) => void;
}) => {
  const [formData, setFormData] = useState(game || {
    opponent: '',
    date: new Date().toISOString().slice(0, 16),
    isHome: true,
    status: 'scheduled'
  });
  // Guards against double-submit. On native (forced Firestore long-polling) the
  // games snapshot can lag after addDoc, so the new game doesn't appear right
  // away; coaches re-tap "Schedule Game", and since addDoc mints a fresh id each
  // time, every tap created a DUPLICATE. Disabling the button while the write is
  // in flight (and closing on success) stops the repeat taps at the source.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (game?.id) {
        const updated = { ...formData, updatedAt: new Date().toISOString() };
        await setDoc(doc(db, `teams/${teamId}/games`, game.id), updated, { merge: true });
        onGameSaved?.({ id: game.id, ...updated });
      } else {
        const newGame = {
          ...formData,
          // Tag the game with the current season so Season History can attribute
          // it. Untagged games still resolve to the current season via the
          // gamesForSeason fallback, and the rollover backfills any that slip
          // through — so this is belt-and-suspenders, not load-bearing.
          ...(team?.season ? { season: team.season, seasonYear: team.seasonYear } : {}),
          createdAt: new Date().toISOString()
        };
        const ref = await addDoc(collection(db, `teams/${teamId}/games`), newGame);
        onGameSaved?.({ id: ref.id, ...newGame });
      }
      onClose();
    } catch (error) {
      setIsSubmitting(false);
      handleFirestoreError(error, OperationType.WRITE, `teams/${teamId}/games`);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 z-50 bg-surface overflow-y-auto p-6 safe-top"
    >
      <div className="max-w-md mx-auto space-y-8 pb-12">
        <header className="flex items-center justify-between">
          <h2 className="text-3xl font-black italic tracking-tight uppercase">
            {game ? 'Edit Game' : 'Schedule Game'}
          </h2>
          <button onClick={onClose} className="p-2 text-ink-2 hover:text-ink">
            <X className="w-6 h-6" />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Opponent</label>
            <input 
              required
              value={formData.opponent}
              onChange={e => setFormData({...formData, opponent: e.target.value})}
              className="w-full bg-canvas border-hairline rounded-xl p-4 outline-none focus:ring-1 focus:ring-emerald-500"
              placeholder="vs White Sox"
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Date & Time</label>
            <input
              required
              type="datetime-local"
              value={formData.date ? formData.date.slice(0, 16) : ''}
              onChange={e => setFormData({...formData, date: e.target.value})}
              className="w-full min-w-0 box-border appearance-none bg-canvas border-hairline rounded-xl p-4 outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Location</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormData({...formData, isHome: true})}
                className={cn(
                  "py-3 rounded-xl text-xs font-black uppercase italic transition-colors",
                  formData.isHome ? "bg-ink text-surface" : "bg-canvas text-ink-2 hover:text-ink"
                )}
              >
                Home
              </button>
              <button
                type="button"
                onClick={() => setFormData({...formData, isHome: false})}
                className={cn(
                  "py-3 rounded-xl text-xs font-black uppercase italic transition-colors",
                  formData.isHome === false ? "bg-ink-mute text-ink" : "bg-canvas text-ink-2 hover:text-ink"
                )}
              >
                Away
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 bg-ink text-surface font-black rounded-2xl shadow-[0_0_30px_rgba(16,185,129,0.2)] uppercase italic disabled:opacity-50"
          >
            {isSubmitting
              ? (game ? 'Updating…' : 'Scheduling…')
              : (game ? 'Update Game' : 'Schedule Game')}
          </button>
        </form>
      </div>
    </motion.div>
  );
};


const EditGamePanel = ({
  game,
  team,
  teamId,
  players,
  onClose,
  onOpenPositions,
  onOpenStats,
}: {
  game: any;
  team: any;
  teamId: string;
  players: any[];
  onClose: () => void;
  onOpenPositions: (game: any) => void;
  onOpenStats: (game: any) => void;
}) => {
  const [opponent, setOpponent] = useState(game.opponent || '');
  const [date, setDate] = useState(game.date ? game.date.slice(0, 16) : '');
  const [awayScore, setAwayScore] = useState(
    game.result ? (game.isHome ? game.result.opponentScore : game.result.myScore).toString() : ''
  );
  const [homeScore, setHomeScore] = useState(
    game.result ? (game.isHome ? game.result.myScore : game.result.opponentScore).toString() : ''
  );
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingScore, setSavingScore] = useState(false);
  const [savingBalls, setSavingBalls] = useState(false);
  const [gameBallIds, setGameBallIds] = useState<string[]>(game.gameBallRecipients || []);

  const trackGameBalls = team.settings?.trackGameBalls ?? false;
  const gameRef = doc(db, `teams/${teamId}/games`, game.id);

  // Recall AI insights saved at import time (read-only — never re-calls the API).
  const [savedInsights, setSavedInsights] = useState<InsightCardData[]>([]);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const snap = await getDocs(collection(db, `teams/${teamId}/games/${game.id}/insights`));
        if (live) setSavedInsights(snap.docs.map(d => ({ id: d.id, ...d.data() }) as InsightCardData));
      } catch {
        if (live) setSavedInsights([]);
      }
    })();
    return () => { live = false; };
  }, [teamId, game.id]);

  const sortedPlayers = [...players].sort((a, b) => {
    const aGB = a.seasonGameBalls ?? 0;
    const bGB = b.seasonGameBalls ?? 0;
    if (aGB !== bGB) return aGB - bGB;
    return (a.name || '').localeCompare(b.name || '');
  });

  const toggleBall = (pid: string) =>
    setGameBallIds(prev => prev.includes(pid) ? prev.filter(id => id !== pid) : [...prev, pid]);

  const saveInfo = async () => {
    setSavingInfo(true);
    try {
      await setDoc(gameRef, { opponent, date, updatedAt: new Date().toISOString() }, { merge: true });
    } finally {
      setSavingInfo(false);
    }
  };

  const saveScore = async () => {
    const awayNum = parseInt(awayScore) || 0;
    const homeNum = parseInt(homeScore) || 0;
    const myScore = game.isHome ? homeNum : awayNum;
    const opponentScore = game.isHome ? awayNum : homeNum;
    const outcome = myScore > opponentScore ? 'W' : myScore < opponentScore ? 'L' : 'T';
    setSavingScore(true);
    try {
      const wasAlreadyComplete = game.status === 'completed';
      await setDoc(gameRef, {
        result: { myScore, opponentScore, outcome },
        status: 'completed',
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      // Only on the transition to completed. This handler doubles as "edit the
      // final score", so firing unconditionally would count one game many times
      // and quietly inflate the activation funnel.
      if (!wasAlreadyComplete) {
        track('game_logged', { source: 'score_entry', outcome });
      }
    } finally {
      setSavingScore(false);
    }
  };

  const saveGameBalls = async () => {
    setSavingBalls(true);
    try {
      const batch = (await import('firebase/firestore')).writeBatch(db);
      batch.set(gameRef, { gameBallRecipients: gameBallIds, updatedAt: new Date().toISOString() }, { merge: true });
      for (const pid of gameBallIds) {
        const player = players.find((p: any) => p.id === pid);
        if (!player) continue;
        const priorLog: Record<string, number> = player.gameBallLog ?? {};
        const newLog = { ...priorLog, [game.id]: 1 };
        const seasonGameBalls = Object.values(newLog).reduce((sum: number, n) => sum + (n as number), 0);
        const pRef = doc(db, `teams/${teamId}/players`, pid);
        batch.set(pRef, { gameBallLog: newLog, seasonGameBalls, updatedAt: new Date().toISOString() }, { merge: true });
      }
      // Also clear any previously awarded players who were deselected
      for (const player of players) {
        if (!gameBallIds.includes(player.id) && (player.gameBallLog?.[game.id])) {
          const priorLog: Record<string, number> = { ...player.gameBallLog };
          delete priorLog[game.id];
          const seasonGameBalls = Object.values(priorLog).reduce((sum: number, n) => sum + (n as number), 0);
          const pRef = doc(db, `teams/${teamId}/players`, player.id);
          batch.set(pRef, { gameBallLog: priorLog, seasonGameBalls, updatedAt: new Date().toISOString() }, { merge: true });
        }
      }
      await batch.commit();
    } finally {
      setSavingBalls(false);
    }
  };

  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="bg-canvas rounded-2xl border border-hairline p-5 space-y-4">
      <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">{title}</p>
      {children}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 z-50 bg-surface overflow-y-auto p-6 safe-top"
    >
      <div className="max-w-md mx-auto space-y-5 pb-16">
        <header className="flex items-center justify-between">
          <h2 className="text-3xl font-black italic tracking-tight uppercase">Edit Game</h2>
          <button onClick={onClose} className="p-2 text-ink-2 hover:text-ink">
            <X className="w-6 h-6" />
          </button>
        </header>

        {/* AI Insights — recalled from the scorebook import (read-only). Hidden
            when a game has none (imported before the feature, or saved without). */}
        {savedInsights.length > 0 && (
          <Section title="Game Insights">
            <div className="space-y-4">
              {groupInsightsByInning(savedInsights).map((group, gi) => (
                <div key={gi} className="space-y-2">
                  {group.label && <p className="text-[11px] font-semibold text-ink-2 uppercase tracking-widest px-1">{group.label}</p>}
                  {group.items.map((ins, i) => <GameInsightCard key={ins.id || i} insight={ins} />)}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Game Info */}
        <Section title="Game Info">
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-1">Opponent</label>
              <input
                value={opponent}
                onChange={e => setOpponent(e.target.value)}
                className="w-full bg-surface border border-hairline rounded-xl px-4 py-3 text-ink outline-none focus:border-ink transition-colors font-bold"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-1">Date & Time</label>
              {/* iOS WKWebView: datetime-local has an intrinsic min-width and
                  ignores w-full, overflowing its container and clipping the right
                  border. min-w-0 + box-border + appearance-none make it respect
                  the box. */}
              <input
                type="datetime-local"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="w-full min-w-0 box-border appearance-none bg-surface border border-hairline rounded-xl px-4 py-3 text-ink outline-none focus:border-ink transition-colors font-bold"
              />
            </div>
          </div>
          <button
            onClick={saveInfo}
            disabled={savingInfo}
            className="w-full py-3 bg-raised text-ink font-black rounded-xl uppercase italic hover:bg-hairline transition-colors disabled:opacity-50 text-sm"
          >
            {savingInfo ? 'Saving...' : 'Save Info'}
          </button>
        </Section>

        {/* Score */}
        <Section title="Score">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-1">
                {game.isHome ? game.opponent : team.name} (Away)
              </label>
              <input
                type="number"
                value={awayScore}
                onChange={e => setAwayScore(e.target.value)}
                className="w-full bg-surface border border-hairline rounded-xl px-4 py-3 text-ink font-black text-2xl text-center outline-none focus:border-ink transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-ink-2 uppercase tracking-widest mb-1">
                {game.isHome ? team.name : game.opponent} (Home)
              </label>
              <input
                type="number"
                value={homeScore}
                onChange={e => setHomeScore(e.target.value)}
                className="w-full bg-surface border border-hairline rounded-xl px-4 py-3 text-ink font-black text-2xl text-center outline-none focus:border-ink transition-colors"
              />
            </div>
          </div>
          <button
            onClick={saveScore}
            disabled={savingScore || !awayScore || !homeScore}
            className="w-full py-3 bg-raised text-ink font-black rounded-xl uppercase italic hover:bg-hairline transition-colors disabled:opacity-50 text-sm"
          >
            {savingScore ? 'Saving...' : 'Save Score'}
          </button>
        </Section>

        {/* Positions */}
        <Section title="Positions">
          <button
            onClick={() => { onOpenPositions(game); onClose(); }}
            className="w-full flex items-center justify-between px-4 py-3 bg-surface border border-hairline rounded-xl hover:border-ink-mute transition-colors"
          >
            <div className="flex items-center space-x-3">
              <Layout className="w-4 h-4 text-ink-2" />
              <span className="font-black text-sm uppercase italic text-ink">Edit Positions</span>
            </div>
            <ChevronRight className="w-4 h-4 text-ink-2" />
          </button>
        </Section>

        {/* Stats — one entry point; the hub offers PDFs + CSV + box score */}
        <Section title="Stats">
          <button
            onClick={() => { onOpenStats(game); onClose(); }}
            className="w-full flex items-center justify-between px-4 py-3 bg-surface border border-hairline rounded-xl hover:border-ink-mute transition-colors"
          >
            <div className="flex items-center space-x-3">
              {game.stats && Object.keys(game.stats).length > 0
                ? <ClipboardList className="w-4 h-4 text-accent-ink" />
                : <FileUp className="w-4 h-4 text-ink-2" />}
              <span className="font-black text-sm uppercase italic text-ink">Import Game Data</span>
            </div>
            <ChevronRight className="w-4 h-4 text-ink-2" />
          </button>
        </Section>

        {/* Game Balls */}
        {trackGameBalls && (
          <Section title="Game Ball">
            <p className="text-[10px] text-ink-2 font-semibold uppercase tracking-widest -mt-2">
              Select recipients · players without one appear first
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {sortedPlayers.map((p: any) => {
                const selected = gameBallIds.includes(p.id);
                const gbCount = p.seasonGameBalls ?? 0;
                return (
                  <button
                    key={p.id}
                    onClick={() => toggleBall(p.id)}
                    className={cn(
                      'w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-colors text-left',
                      selected ? 'bg-gold/15 border-hairline' : 'bg-surface border-hairline hover:border-ink-mute'
                    )}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-7 h-7 bg-raised rounded-full flex items-center justify-center font-black text-[10px] shrink-0">
                        #{p.number}
                      </div>
                      <span className={cn('font-black text-sm uppercase', selected ? 'text-gold-ink' : 'text-ink')}>
                        {p.name}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">
                        {gbCount === 0 ? 'none yet' : `${gbCount} this season`}
                      </span>
                      <div className={cn(
                        'w-5 h-5 rounded-full border-2 flex items-center justify-center text-[10px] transition-colors',
                        selected ? 'bg-gold border-amber-500 text-surface' : 'border-ink-mute'
                      )}>
                        {selected && '✓'}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <button
              onClick={saveGameBalls}
              disabled={savingBalls}
              className="w-full py-3 bg-gold text-surface font-black rounded-xl uppercase italic hover:bg-gold-ink transition-colors disabled:opacity-50 text-sm"
            >
              {savingBalls ? 'Saving...' : 'Save Game Balls'}
            </button>
          </Section>
        )}
      </div>
    </motion.div>
  );
};


const Dashboard = () => {
  const { user, coachData } = useContext(AuthContext);
  const [teams, setTeams] = useState<any[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  // Lets a teamless coach (e.g. one who deleted their last team to close their
  // account) reach account deletion from the welcome screen — otherwise the only
  // entry point is the in-team account menu, creating a can't-delete loop.
  const [showWizardAccount, setShowWizardAccount] = useState(false);
  const [wizardAccountMenuOpen, setWizardAccountMenuOpen] = useState(false);
  // After the wizard creates a team we hold on a "setting up" screen until the
  // new team + its roster have actually synced from Firestore. Without this, the
  // dashboard renders before the team-scoped listeners (players/games) attach
  // and deliver — on native (long-polling) that left a blank roster/games until
  // a hard refresh. Initialized from localStorage because on native the auth
  // user can re-fire and remount <Dashboard> right after onboarding, wiping
  // in-memory state; the marker survives that remount.
  const [settingUpTeamId, setSettingUpTeamId] = useState<string | null>(
    () => { try { return localStorage.getItem('dugout_justOnboarded'); } catch { return null; } }
  );
  const team = teams.find(t => t.id === selectedTeamId) ?? null;
  const isHeadCoach = !team || team.coachId === user?.uid;
  const isAssistantCoach = !!team && !isHeadCoach && (team.coaches ?? []).includes(user?.uid ?? '');
  const { isElite, isLoading: subLoading, seenTrialOffer, eliteSources, grantExpiresAt } = useSubscription(user?.uid);

  // Sync head coach's Elite status to the team document so assistant coaches can inherit it.
  useEffect(() => {
    if (!team || !isHeadCoach || subLoading) return;
    updateDoc(doc(db, 'teams', team.id), { isElite });
  }, [isElite, team?.id, isHeadCoach, subLoading]);

  // Assistant coaches inherit Elite status from the head coach via the team document.
  const effectiveIsElite = team?.isDemoSession ? true : (isHeadCoach ? isElite : (team?.isElite ?? false));

  // ── Analytics ──────────────────────────────────────────────────────────────
  // Demo sessions are screenshot scaffolding, so drop them before anything is
  // recorded — the same reason nightlyUsageRollup filters them out of every count.
  useEffect(() => {
    if (team?.isDemoSession) suppressAnalytics();
  }, [team?.isDemoSession]);

  // Stripe Checkout leaves the app, so the conversion can only be confirmed on
  // return, once entitlement actually resolves. No-ops unless a checkout is
  // pending in this tab.
  useEffect(() => {
    if (subLoading) return;
    resolvePendingCheckout(isElite);
  }, [isElite, subLoading]);

  // Funnel dimensions, mirroring the Loops contact properties and the rollup's
  // `mix` buckets so all three describe the same populations. Nothing identifying.
  useEffect(() => {
    if (!team || team.isDemoSession) return;
    setAnalyticsUser({
      styleOfPlay: team.settings?.styleOfPlay ?? team.styleOfPlay,
      ageGroup: team.ageGroup,
      org: team.settings?.tournamentOrg ?? team.org,
      isHeadCoach,
      isElite: effectiveIsElite,
    });
  }, [team?.id, team?.ageGroup, isHeadCoach, effectiveIsElite]);

  // Lazy cleanup: if this user's demo session has expired, delete their data and sign out.
  useEffect(() => {
    if (!team?.isDemoSession || !team?.expiresAt) return;
    if (new Date(team.expiresAt) > new Date()) return;
    cleanupExpiredDemoSession(team.id).catch(console.error);
  }, [team?.id, team?.isDemoSession, team?.expiresAt]);

  const [eliteModal, setEliteModal] = useState<'onboarding' | 'upgrade' | null>(null);
  const [players, setPlayers] = useState<any[]>([]);
  const [games, setGames] = useState<any[]>([]);
  // The onboarding CSV's stats container, kept aside so the signup-overlap
  // check below can see it (it is filtered out of `games`).
  const [onboardingStatsGame, setOnboardingStatsGame] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState(() => {
    // If we just finished onboarding (marker survives a native remount), always
    // open on Home — never a stale tab from a prior session. This wins over the
    // persisted activeTab, which the remount churn could otherwise restore.
    try { if (localStorage.getItem('dugout_justOnboarded')) return 'dashboard'; } catch {}
    try { return localStorage.getItem('dugout_activeTab') || 'dashboard'; } catch { return 'dashboard'; }
  });
  const [autoOpenSmartScout, setAutoOpenSmartScout] = useState(false);
  const [isAddingPlayer, setIsAddingPlayer] = useState(false);
  const [showRetired, setShowRetired] = useState(false);
  const [isAddingGuestPlayer, setIsAddingGuestPlayer] = useState(false);
  const [isImportingCalendar, setIsImportingCalendar] = useState(false);
  // Games-tab header popover menus (Import / Add). One open at a time.
  const [importMenuOpen, setImportMenuOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [showScoutingSession, setShowScoutingSession] = useState(false);
  const [showSeasonStatsImport, setShowSeasonStatsImport] = useState(false);
  // The one GameChanger import screen. `game` = opened from that game's card
  // (the import bonds to it); null = from the menu or the share sheet, where the
  // files themselves say which game they are.
  const [gcImport, setGcImport] = useState<{ game: any | null } | null>(null);
  // Files the iOS share extension handed over; a new nonce is a new batch, which
  // the open import screen adds to whatever the coach has already sent.
  const [gcIncoming, setGcIncoming] = useState<{ files: File[]; nonce: number } | null>(null);
  const shareNonce = useRef(0);
  // Read-only box score for a game that has stats; "Update Stats" opens the import.
  const [boxScoreGame, setBoxScoreGame] = useState<any>(null);
  const openGameImport = (g: any | null = null) => { setBoxScoreGame(null); setGcImport({ game: g }); };
  // A batch belongs to the import screen it was handed to. Dropping it on close
  // keeps the next screen (a fresh mount, possibly for another game) from
  // replaying files the coach already dealt with.
  const closeGameImport = () => { setGcImport(null); setGcIncoming(null); };
  const openGameData = (g: any) =>
    g?.stats && Object.keys(g.stats).length > 0 ? setBoxScoreGame(g) : openGameImport(g);
  const [insightsGame, setInsightsGame] = useState<any>(null);
  const [showChecklistCalendarSync, setShowChecklistCalendarSync] = useState(false);
  // Welcome card visibility — dismissed via the X button (transient, this view
  // only). Combined with a per-team localStorage flag (`dugout_welcomeSeen_*`)
  // so the card stays until the coach explicitly dismisses it OR they come
  // back in a future session. No more 2-minute wall-clock timer.
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);

  // Captures the "has the coach ever seen the welcome card for this team?"
  // value ONCE per team (memoized on team.id). We can't read localStorage
  // freshly on every render — React strict mode double-renders, and we mark
  // the flag in a useEffect below, which would cause the second render of
  // the same mount to see "already seen" and hide the card before the user
  // ever sees it. Returns null while team is still loading.
  const welcomeInitialUnseen = useMemo(() => {
    if (!team?.id) return null;
    try { return localStorage.getItem(`dugout_welcomeSeen_${team.id}`) !== 'true'; }
    catch { return false; }
  }, [team?.id]);

  // Persist the "seen" flag after the first render where the welcome would be
  // shown. Runs once per team mount — future sessions will see the flag and
  // skip the card. Idempotent; safe under strict mode's double-effect.
  useEffect(() => {
    if (welcomeInitialUnseen === true && team?.id) {
      try { localStorage.setItem(`dugout_welcomeSeen_${team.id}`, 'true'); } catch {}
    }
  }, [welcomeInitialUnseen, team?.id]);

  // iOS share extension: a coach shares a GameChanger file (scorebook PDF, game
  // stats PDF or stats CSV) to "Import to Dugout"; the extension stages it in the
  // App Group and tries to open dugout://import. We drain the inbox on that URL
  // open, on every resume (iOS may not let the extension switch apps, so the
  // coach often comes back by hand), and once on mount for a cold start.
  //
  // This is the ONLY place the inbox is drained, so a file can't be taken by one
  // listener and dropped by another. Each batch goes to the import screen that's
  // already open (adding to the files sent before it) or opens one. The Elite
  // check lives in the screen, which holds the files rather than discarding them
  // while the subscription is still loading.
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !team) return;

    const tryDrain = async () => {
      const files = await drainSharedFiles();
      if (!files.length) return;
      setGcIncoming({ files, nonce: ++shareNonce.current });
      setBoxScoreGame(null);
      setGcImport(cur => cur ?? { game: null });
    };

    let urlSub: { remove: () => void } | undefined;
    let resumeSub: { remove: () => void } | undefined;
    (async () => {
      urlSub = await CapApp.addListener('appUrlOpen', (data) => {
        if (data.url?.startsWith('dugout://import')) void tryDrain();
      });
      resumeSub = await CapApp.addListener('resume', () => { void tryDrain(); });
    })();
    // Safety net: app may have been cold-launched by the share open.
    void tryDrain();

    return () => { urlSub?.remove(); resumeSub?.remove(); };
  }, [team?.id]);

  // Post-game Scout nudge tap → land the coach in the scorebook import flow,
  // mirroring the share-extension path above (Elite gate included). A cold-start
  // tap that beats listener registration just opens the app normally — fine for
  // a nudge.
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !team) return;
    let tapSub: { remove: () => void } | null = null;
    (async () => {
      tapSub = await addScoutNudgeTapListener(() => {
        if (!effectiveIsElite) {
          setEliteModal('upgrade');
          return;
        }
        openGameImport(null);
      });
    })();
    return () => { tapSub?.remove(); };
  }, [team?.id, effectiveIsElite]);

  // Derive `firstGamePlanned` from the actual game data. Marks the checklist
  // item complete when ANY game has a saved `plan` field on it. Previously we
  // marked this on PlanGameView close — which lit up the checkbox even when
  // the coach dismissed without saving. This is the honest version: the box
  // checks off iff a plan was actually persisted by PlanGameView's handleSave.
  useEffect(() => {
    if (!team?.id) return;
    const cl = team?.settings?.onboardingChecklist;
    if (!cl || cl.firstGamePlanned) return;
    const anyPlanned = games.some((g: any) => g.plan && Object.keys(g.plan).length > 0);
    if (anyPlanned) {
      updateDoc(doc(db, 'teams', team.id), {
        'settings.onboardingChecklist.firstGamePlanned': true,
      }).catch(() => {});
    }
  }, [team?.id, team?.settings?.onboardingChecklist?.firstGamePlanned, games]);
  const [scoutingEliteRanked, setScoutingEliteRanked] = useState<any[] | null>(null);
  const [calendarSyncSuccessCount, setCalendarSyncSuccessCount] = useState<number | null>(null);
  const [editingPlayer, setEditingPlayer] = useState<any>(null);
  // Which tab the open player card is on — kept in the parent so swiping to the
  // next/prev player (which remounts PlayerForm) can preserve the coach's tab.
  const [playerCardTab, setPlayerCardTab] = useState<'stats' | 'attributes' | 'positions'>('stats');
  const [scoutingPlayer, setScoutingPlayer] = useState<any>(null);
  const [activeGame, setActiveGame] = useState<any>(null);
  // True while any game write is saved on this phone but not yet uploaded
  // (no signal at the field). Drives the SyncNote rows.
  const [gamesPendingSync, setGamesPendingSync] = useState(false);
  const [activeLineupGame, setActiveLineupGame] = useState<any>(null);
  const [activePlanGame, setActivePlanGame] = useState<any>(null);
  // A FINAL game's positions open a cell-by-cell editor, never the planner,
  // which can rebuild every inning over what really happened (support log G2).
  const [positionsEditorGame, setPositionsEditorGame] = useState<any>(null);
  const [planGameInitialTab, setPlanGameInitialTab] = useState<'gameplan' | 'bullpen' | 'scouting' | 'lineup' | 'defense'>('gameplan');
  const openPlanGame = (game: any, tab: 'gameplan' | 'bullpen' | 'scouting' | 'lineup' | 'defense' = 'gameplan') => {
    setPlanGameInitialTab(tab);
    setActivePlanGame(game);
  };
  const [isAddingGame, setIsAddingGame] = useState(false);
  const [editingGame, setEditingGame] = useState<any>(null);
  const [editingTournament, setEditingTournament] = useState<any>(null);
  const [isCreatingTournament, setIsCreatingTournament] = useState(false);
  const [activeTournament, setActiveTournament] = useState<any>(null);
  const [tournaments, setTournaments] = useState<any[]>([]);
  const [activeStrategyGame, setActiveStrategyGame] = useState<any>(null);
  const [showSeasonHistory, setShowSeasonHistory] = useState(false);
  const [showWrapped, setShowWrapped] = useState(false);
  const [enterResultsGame, setEnterResultsGame] = useState<any>(null);
  const [editGamePanelGame, setEditGamePanelGame] = useState<any>(null);
  const [showDevReview, setShowDevReview] = useState(false);
  const nextGameDividerRef = useRef<HTMLDivElement>(null);
  const gamesScrollContainerRef = useRef<HTMLElement>(null);
  const hasScrolledGamesRef = useRef(false);

  useEffect(() => {
    localStorage.setItem('dugout_activeTab', activeTab);
  }, [activeTab]);

  // Demo sessions always open on the home tab regardless of what localStorage has.
  useEffect(() => {
    if (team?.isDemoSession) setActiveTab('dashboard');
  }, [team?.isDemoSession]);

  useEffect(() => {
    if (activeTab !== 'games') {
      hasScrolledGamesRef.current = false;
      return;
    }
    if (hasScrolledGamesRef.current) return;
    if (games.length === 0 && tournaments.length === 0) return;
    setTimeout(() => {
      const divider = nextGameDividerRef.current;
      const container = gamesScrollContainerRef.current;
      if (divider && container) {
        const dividerRect = divider.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const offset = dividerRect.top - containerRect.top + container.scrollTop;
        container.scrollTop = offset - container.clientHeight / 2 + divider.offsetHeight / 2;
        hasScrolledGamesRef.current = true;
      }
    }, 100);
  }, [activeTab, games, tournaments]);

  // Pre-game reminders (native only; no-ops on web). The games onSnapshot
  // re-fires this on every create/edit/delete/completion, so the pending
  // notifications always mirror the schedule. Fire-and-forget, best-effort.
  useEffect(() => {
    reconcileGameReminders(games, team?.settings?.preGameRemindersEnabled ?? true);
    // Post-game Scout nudge: stamp each game's nudge fire time (all platforms —
    // the device knows the local timezone, the server doesn't), then re-attempt
    // push registration in case the reminder pass above just won the OS
    // notification permission.
    void reconcileScoutNudgeTimes(games, team);
    if (user?.uid && !user.isAnonymous) void initPushRegistration(user.uid);
  }, [games, team?.settings?.preGameRemindersEnabled]);

  // Mirror the team's haptics preference into the native wrapper's global switch
  // so every buzz respects it from app load (default ON when unset). No-op on web.
  useEffect(() => {
    setHapticsEnabled(team?.settings?.hapticsEnabled ?? true);
  }, [team?.settings?.hapticsEnabled]);

  const deleteGame = async (gameId: string) => {
    if (!team) return;
    try {
      // 1. Delete the game itself
      await deleteDoc(doc(db, `teams/${team.id}/games`, gameId));

      // 2. Clean up the "Ghost Logs" in player data
      const playersWithLogs = players.filter(p => 
        p.pitchingLog?.some((log: any) => log.gameId === gameId)
      );

      for (const player of playersWithLogs) {
        const updatedLog = player.pitchingLog.filter((log: any) => log.gameId !== gameId);
        await setDoc(doc(db, `teams/${team.id}/players`, player.id), {
          pitchingLog: updatedLog
        }, { merge: true });
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `teams/${team.id}/games/${gameId}`);
    }
  };

  // Scope season-wide aggregates (record, leaders, player season stats) to the
  // team's CURRENT season. After a "Start New Season" rollover, prior-season
  // games remain in `games` (season-tagged) but must not count toward the new
  // season's record/leaders. Untagged legacy games resolve to current via the
  // gamesForSeason fallback. The raw `games` array still drives the schedule,
  // lineup builder, and pitcher eligibility unchanged.
  const seasonGames = useMemo(() => currentSeasonGames(games, team), [games, team?.season, team?.seasonYear]);

  const completedGames = seasonGames.filter(g => g.status === 'completed' && g.result);

  // Which games have saved AI insights, for the game card's Insights button.
  // Imports since 2026-10-13 put a count on the game doc. Earlier ones (and the
  // demo seed) only wrote the subcollection, so look those up once per session.
  // Read-only: nothing is written back.
  const [legacyInsightIds, setLegacyInsightIds] = useState<Set<string>>(new Set());
  const insightsProbed = useRef(new Set<string>());
  useEffect(() => {
    if (!team?.id) return;
    const toProbe = games.filter(g => g.status === 'completed' && !g.gcImport?.insights && !insightsProbed.current.has(g.id));
    if (!toProbe.length) return;
    toProbe.forEach(g => insightsProbed.current.add(g.id));
    Promise.all(toProbe.map(g =>
      getDocs(query(collection(db, `teams/${team.id}/games/${g.id}/insights`), limit(1)))
        .then(snap => (snap.empty ? null : g.id))
        .catch(() => null),
    )).then(ids => {
      const found = ids.filter(Boolean) as string[];
      if (found.length) setLegacyInsightIds(prev => new Set([...prev, ...found]));
    });
  }, [games, team?.id]);
  const gameHasInsights = (g: any) => (g.gcImport?.insights?.count ?? 0) > 0 || legacyInsightIds.has(g.id);
  // seasonStartRecord is set during the wizard when a coach joins mid-season
  // with a CSV import. It's a baseline that gets added to live W/L/T counts
  // computed from completed games — so a 5-2 team displays 5-2 immediately
  // and becomes 6-2 after a fresh W.
  const startW = team?.seasonStartRecord?.w ?? 0;
  const startL = team?.seasonStartRecord?.l ?? 0;
  const startT = team?.seasonStartRecord?.t ?? 0;
  const wins = startW + completedGames.filter(g => g.result.outcome === 'W').length;

  // Full season stat aggregates for every player — drives Roster cards, Team Leaders, and dashboard stats
  const playerSeasonStats = useMemo(() => {
    type AggStats = {
      pa: number; ab: number; h: number; r: number; rbi: number; xbh: number; sb: number; bb: number; so: number;
      avg: number; ops: number; obp: number; slg: number;
      ip: number; p_k: number;
      era: number; whip: number; sPct: number; fpsPct: number;
      totalHR: number; totalHBP: number; totalBF: number;
    };
    const map = new Map<string, AggStats>();
    const gamesWithStats = seasonGames.filter(g => g.stats && Object.keys(g.stats).length > 0);
    const allIds = new Set<string>(gamesWithStats.flatMap(g => Object.keys(g.stats)));

    allIds.forEach(pid => {
      let pa = 0, ab = 0, h = 0, r = 0, rbi = 0, xbh = 0, sb = 0, bb = 0, so = 0;
      let sum_avg = 0, total_ab_avg = 0;
      let sum_ops = 0, total_pa_ops = 0;
      let sum_obp = 0, total_pa_obp = 0;
      let sum_slg = 0, total_ab_slg = 0;
      let ip = 0, p_k = 0;
      let sum_era = 0, total_ip_era = 0;
      let sum_whip = 0, total_ip_whip = 0;
      let sum_sPct = 0, total_pitches = 0;
      let sum_fpsPct = 0, total_bf = 0;
      let totalHR = 0, totalHBP = 0, totalBF = 0;

      gamesWithStats.forEach(g => {
        const s = g.stats[pid];
        if (!s) return;
        if (s.pa > 0 || s.ab > 0) {
          pa += (s.pa || 0); ab += (s.ab || 0); h += (s.h || 0);
          r += (s.r || 0); rbi += (s.rbi || 0); xbh += (s.xbh || 0);
          sb += (s.sb || 0); bb += (s.bb || 0); so += (s.so || 0);
          if (s.avg != null) { sum_avg += s.avg * (s.ab || 1); total_ab_avg += (s.ab || 1); }
          if (s.ops != null) { sum_ops += s.ops * (s.pa || 1); total_pa_ops += (s.pa || 1); }
          if (s.obp != null) { sum_obp += s.obp * (s.pa || 1); total_pa_obp += (s.pa || 1); }
          if (s.slg != null) { sum_slg += s.slg * (s.ab || 1); total_ab_slg += (s.ab || 1); }
        }
        if ((s.ip || 0) > 0 || (s.pitches || 0) > 0) {
          ip += (s.ip || 0); p_k += (s.soAllowed || 0);
          total_ip_era += (s.ip || 0); total_ip_whip += (s.ip || 0);
          if (s.era != null) sum_era += s.era * (s.ip || 1);
          if (s.whip != null) sum_whip += s.whip * (s.ip || 1);
          const pitches = s.pitches || 0;
          total_pitches += pitches;
          const bf = Math.round((s.ip || 0) * 4) || 0;
          total_bf += bf;
          const sVal = s.sPct != null ? (s.sPct > 1 ? s.sPct / 100 : s.sPct) : null;
          const fpsVal = s.fpsPct != null ? (s.fpsPct > 1 ? s.fpsPct / 100 : s.fpsPct) : null;
          if (sVal != null) sum_sPct += sVal * (pitches || 1);
          if (fpsVal != null) sum_fpsPct += fpsVal * (bf || 1);
          // FIP / BABIP inputs
          totalHR  += (s.hrAllowed  || 0);
          totalHBP += (s.hbpAllowed || 0);
          totalBF  += (s.bf != null && s.bf > 0)
            ? s.bf
            : Math.round((s.ip || 0) * 3) + (s.hAllowed || 0) + (s.bbAllowed || s.bb || 0) + (s.hbpAllowed || 0);
        }
      });

      map.set(pid, {
        pa, ab, h, r, rbi, xbh, sb, bb, so,
        avg:   total_ab_avg  > 0 ? sum_avg  / total_ab_avg  : 0,
        ops:   total_pa_ops  > 0 ? sum_ops  / total_pa_ops  : 0,
        obp:   total_pa_obp  > 0 ? sum_obp  / total_pa_obp  : 0,
        slg:   total_ab_slg  > 0 ? sum_slg  / total_ab_slg  : 0,
        ip, p_k,
        era:   total_ip_era  > 0 ? sum_era  / total_ip_era  : 0,
        whip:  total_ip_whip > 0 ? sum_whip / total_ip_whip : 0,
        sPct:  total_pitches > 0 ? sum_sPct  / total_pitches : 0,
        fpsPct: total_bf     > 0 ? sum_fpsPct / total_bf    : 0,
        totalHR, totalHBP, totalBF,
      });
    });
    return map;
  }, [seasonGames]);

  const playerFieldTimeStats = useMemo(() => {
    const map = new Map<string, { positions: Record<string, number>; bench: number; gp: number }>();
    const allPositions = ['P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'EH'];
    players.forEach((p: any) => {
      map.set(p.id, { positions: Object.fromEntries(allPositions.map(pos => [pos, 0])), bench: 0, gp: 0 });
    });
    seasonGames
      .filter((g: any) => g.status === 'completed' && g.inningLogs && Object.keys(g.inningLogs).length > 0)
      .forEach((game: any) => {
        const playersInGame = new Set<string>();
        Object.values(game.inningLogs).forEach((inningLineup: any) => {
          inningLineup.forEach((p: any) => {
            if (!map.has(p.id)) return;
            playersInGame.add(p.id);
            const entry = map.get(p.id)!;
            if (p.position === 'BENCH') { entry.bench++; }
            else if (entry.positions[p.position] !== undefined) { entry.positions[p.position]++; }
          });
        });
        playersInGame.forEach((pid: string) => { if (map.has(pid)) map.get(pid)!.gp++; });
      });
    return map;
  }, [seasonGames, players]);

  const playerHotCold = useMemo(
    () => computePlayerHotCold(seasonGames, playerSeasonStats),
    [seasonGames, playerSeasonStats]
  );

  // Top 2 purely-hot players for the home screen Recent Form widget
  const hotPlayers = useMemo(() => {
    const shortName = (name: string) => {
      const parts = (name || '').trim().split(/\s+/);
      return parts.length > 1 ? `${parts[0][0]}. ${parts[parts.length - 1]}` : parts[0];
    };
    return [...playerHotCold.entries()]
      .filter(([, hc]) =>
        (hc.batting === 'hot' || hc.pitching === 'hot') &&
        hc.batting !== 'cold' && hc.pitching !== 'cold'
      )
      .map(([pid, hc]) => {
        const player = players.find((p: any) => p.id === pid);
        return player ? { player, hc, name: shortName(player.name) } : null;
      })
      .filter((r): r is NonNullable<typeof r> => r !== null)
      .sort((a, b) => b.hc.score - a.hc.score)
      .slice(0, 3);
  }, [playerHotCold, players]);

  // Top player in each of 10 fixed categories (5 hitting, 5 pitching)
  const teamLeaders = useMemo(() => {
    const CATEGORIES = [
      { id: 'ops',   label: 'OPS',   stat: 'ops',   higher: true,  minPa: 5, minIp: 0, color: 'emerald', format: (v: number) => v.toFixed(3).replace(/^0/, '') },
      { id: 'avg',   label: 'AVG',   stat: 'avg',   higher: true,  minPa: 5, minIp: 0, color: 'emerald', format: (v: number) => v.toFixed(3).replace(/^0/, '') },
      { id: 'hits',  label: 'Hits',  stat: 'h',     higher: true,  minPa: 0, minIp: 0, color: 'emerald', format: (v: number) => String(v) },
      { id: 'rbi',   label: 'RBI',   stat: 'rbi',   higher: true,  minPa: 0, minIp: 0, color: 'emerald', format: (v: number) => String(v) },
      { id: 'sb',    label: 'SB',    stat: 'sb',    higher: true,  minPa: 0, minIp: 0, color: 'emerald', format: (v: number) => String(v) },
      { id: 'k',     label: 'K',     stat: 'p_k',   higher: true,  minPa: 0, minIp: 0, color: 'sky',     format: (v: number) => String(v) },
      { id: 'ip',    label: 'IP',    stat: 'ip',    higher: true,  minPa: 0, minIp: 0, color: 'sky',     format: (v: number) => v.toFixed(1) },
      { id: 'era',   label: 'ERA',   stat: 'era',   higher: false, minPa: 0, minIp: 1, color: 'sky',     format: (v: number) => v.toFixed(2) },
      { id: 'whip',  label: 'WHIP',  stat: 'whip',  higher: false, minPa: 0, minIp: 1, color: 'sky',     format: (v: number) => v.toFixed(2) },
      { id: 'spct',  label: 'STR%',  stat: 'sPct',  higher: true,  minPa: 0, minIp: 1, color: 'sky',     format: (v: number) => `${Math.round(v * 100)}%` },
    ];

    return CATEGORIES.flatMap(cat => {
      let bestPid: string | null = null;
      let bestVal = cat.higher ? -Infinity : Infinity;
      for (const p of players) {
        const s = playerSeasonStats.get(p.id);
        if (!s) continue;
        if (cat.minPa > 0 && s.pa < cat.minPa) continue;
        if (cat.minIp > 0 && s.ip < cat.minIp) continue;
        const val = (s as any)[cat.stat] as number;
        if (val == null || val === 0) continue;
        if (cat.higher ? val > bestVal : val < bestVal) { bestVal = val; bestPid = p.id; }
      }
      if (bestPid === null) return [];
      return [{ cat, player: players.find(p => p.id === bestPid)!, statValue: bestVal }];
    });
  }, [playerSeasonStats, players]);
  // Ordered active roster — MUST match RosterTab's visible order (non-retired,
  // sorted by OVR desc) so swiping the player card advances in the same order
  // the coach sees on the Roster tab.
  const orderedRosterPlayers = useMemo(
    () => players.filter((p: any) => !p.retired).sort((a: any, b: any) => (b.ovr || 0) - (a.ovr || 0)),
    [players],
  );
  // Everyone actually available to play: no retired players, no deactivated
  // guests. Every game-day and game-planning surface must use this same list —
  // PlanGameView and TournamentView previously took the raw roster, so retired
  // players showed up in batting orders and bench/pitching plans.
  const gameDayPlayers = useMemo(
    () => players.filter((p: any) => !p.retired && (!p.isGuest || p.guestActive !== false)),
    [players],
  );
  // Most recent completed game stats per player — drives "Last game X-for-Y"
  const playerLastGame = useMemo(() => {
    const map = new Map<string, { h: number, ab: number, ip: number, opponent: string }>();
    [...games]
      .filter(g => g.stats)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .forEach(g => {
        Object.entries(g.stats).forEach(([pid, s]: [string, any]) => {
          if (!map.has(pid)) {
            map.set(pid, { h: s.h || 0, ab: s.ab || 0, ip: s.ip || 0, opponent: g.opponent || '' });
          }
        });
      });
    return map;
  }, [games]);
  const losses = startL + completedGames.filter(g => g.result.outcome === 'L').length;
  const ties = startT + completedGames.filter(g => g.result.outcome === 'T').length;
  const recordString = `${wins}-${losses}${ties > 0 ? `-${ties}` : ''}`;

  // Signup totals vs. games logged later: see src/lib/signup-overlap.ts.
  const { recordOverlaps, statsOverlap } = detectSignupOverlap(team, completedGames, onboardingStatsGame);
  const showSignupOverlap = isHeadCoach && (recordOverlaps || statsOverlap) && !team?.settings?.signupOverlapReviewed;
  const [clearingSignup, setClearingSignup] = useState(false);
  const resolveSignupOverlap = async (clear: boolean) => {
    if (!team?.id) return;
    setClearingSignup(true);
    try {
      const batch = writeBatch(db);
      // One write per document: Firestore rejects a batch that touches the
      // same doc twice, so the team doc's two changes go in together.
      batch.update(doc(db, 'teams', team.id), {
        'settings.signupOverlapReviewed': true,
        ...(clear && recordOverlaps ? { seasonStartRecord: deleteField() } : {}),
      });
      if (clear && statsOverlap && onboardingStatsGame) {
        // Drop the container, then rebuild each affected player's cached season
        // stats from the games that remain (the same aggregation the GameChanger
        // import uses). xOVR is recomputed from this cache on load.
        batch.delete(doc(db, `teams/${team.id}/games`, onboardingStatsGame.id));
        const snap = await getDocs(collection(db, `teams/${team.id}/games`));
        const lines = statLinesWithoutSignup(snap.docs.map(d => ({ id: d.id, ...d.data() })), onboardingStatsGame);
        for (const [pid, series] of Object.entries(lines)) {
          // Skip players no longer on the roster: updating a deleted doc
          // would fail the whole batch.
          if (!players.some((p: any) => p.id === pid)) continue;
          const rebuilt = Object.fromEntries(
            Object.entries(aggregatePlayerStats(series)).filter(([, v]) => v !== undefined)
          );
          batch.update(doc(db, `teams/${team.id}/players`, pid), { stats: rebuilt, updatedAt: new Date().toISOString() });
        }
      }
      await batch.commit();
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `teams/${team.id}`);
    } finally {
      setClearingSignup(false);
    }
  };

  // Whether any season has been archived by a "Start New Season" rollover. Gates
  // the "View past seasons" link so a first-season team never sees a dead end.
  const hasArchivedSeasons = useMemo(
    () => players.some((p: any) => Object.keys(p?.seasonStats ?? {}).length > 0),
    [players]
  );

  // Development Review: players where xOVR has diverged ±4+ pts over 4+ games.
  // Only head coaches see this — assistant coaches can't write attributes anyway.
  // Scoped to seasonGames: a proposal rewrites scouting attributes, so it must be
  // earned by this season's play, not carried over from a prior season.
  const devReviewProposals = useMemo(() =>
    isHeadCoach ? computeDevReviewProposals(players, seasonGames) : [],
    [players, seasonGames, isHeadCoach]
  );

  useEffect(() => {
    if (!user) return;

    // Merge results from two queries: teams where I'm head coach, and teams where I'm in coaches array.
    // Each query's results are held separately and fully REPLACED on every snapshot, so a deleted
    // team actually leaves the merged list. (A single shared, additive map would never drop deletions,
    // leaving the app pointed at a team whose doc is gone → white-page crash on delete.)
    let q1Teams: any[] = [];
    let q2Teams: any[] = [];
    let q1Done = false;
    let q2Done = false;
    // Whether each query's latest result came only from the local cache, i.e.
    // the server hasn't confirmed it. Offline, an empty cache answers "no teams".
    let q1FromCache = true;
    let q2FromCache = true;

    const reconcile = () => {
      if (!q1Done || !q2Done) return;
      const teamMap = new Map<string, any>();
      [...q1Teams, ...q2Teams].forEach(t => teamMap.set(t.id, t));
      const merged = Array.from(teamMap.values());
      // No signal + nothing cached reads as "this coach has no teams", which used
      // to open the create-team wizard at the field. Only trust an empty result
      // the server has confirmed; until then the loading screen stays up.
      if (merged.length === 0 && (q1FromCache || q2FromCache)) return;
      setTeams(merged);
      if (merged.length === 0) {
        // Clear the now-dangling selection so `team` resolves to null cleanly and
        // nothing tries to render against the just-deleted team.
        setSelectedTeamId(null);
        // Anonymous users with no teams have a stale demo session — sign them out
        // so they land on the login screen rather than the create/join wizard.
        if (user.isAnonymous) { signOut(auth); return; }
        // A coach with no teams goes to the wizard. (Previously this bailed for
        // unverified password accounts on the assumption they'd never reach here
        // without a team — but deleting your only team breaks that, leaving a
        // blank page. The wizard is always a safe, visible destination; the
        // Login component's awaitingVerification screen handles fresh signups.)
        setShowWizard(true);
      } else {
        setSelectedTeamId(prev => {
          if (prev && merged.find((t: any) => t.id === prev)) return prev;
          return merged.sort((a: any, b: any) =>
            new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
          )[0].id;
        });
        // Do NOT force the wizard closed here. When a coach finishes the wizard,
        // handleCreate writes the team, which fires THIS very snapshot — and if
        // we dismissed the wizard now, we'd skip the arrival screen AND the
        // onComplete handler that sets the Home tab + the "setting up" screen,
        // dumping the coach on whatever tab they were last on (e.g. Settings).
        // The wizard owns its own dismissal via onComplete. (A coach who isn't in
        // the wizard already has showWizard=false, so leaving it as-is is safe.)
      }
    };

    // includeMetadataChanges so the server's confirmation of a cache-only result
    // still arrives (it changes no documents, so it's otherwise swallowed). Other
    // metadata-only events (a pending write acking) are skipped as before.
    const q1 = query(collection(db, 'teams'), where('coachId', '==', user.uid));
    const unsub1 = onSnapshot(q1, { includeMetadataChanges: true }, (snap) => {
      if (q1Done && snap.docChanges().length === 0 && snap.metadata.fromCache === q1FromCache) return;
      q1Teams = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      q1FromCache = snap.metadata.fromCache;
      q1Done = true;
      reconcile();
    });

    const q2 = query(collection(db, 'teams'), where('coaches', 'array-contains', user.uid));
    const unsub2 = onSnapshot(q2, { includeMetadataChanges: true }, (snap) => {
      if (q2Done && snap.docChanges().length === 0 && snap.metadata.fromCache === q2FromCache) return;
      q2Teams = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      q2FromCache = snap.metadata.fromCache;
      q2Done = true;
      reconcile();
    }, () => { q2Teams = []; q2FromCache = false; q2Done = true; reconcile(); });

    return () => { unsub1(); unsub2(); };
  }, [user?.uid]);

  useEffect(() => {
    if (!team?.id) return;

    const unsubPlayers = onSnapshot(collection(db, `teams/${team.id}/players`), (snap) => {
      // First-team onboarding race: a brand-new team's very first players
      // listener can fire from the local cache BEFORE the just-written roster
      // has propagated, delivering an empty snapshot that would wipe the
      // players App.tsx pre-seeded from the wizard (onComplete → setPlayers).
      // On iOS this is worse because native forces Firestore long-polling.
      // Ignore an empty snapshot that is still cache-only / has unacked writes;
      // a genuinely empty roster arrives confirmed (fromCache false) and is
      // applied normally. This only ever protects a non-empty pre-seed.
      if (snap.empty && snap.metadata.fromCache) return;
      setPlayers(snap.docs.map(d => {
        const raw = d.data();
        // Normalize legacy attribute names (velo/ctrl/aggro → velocity/control/approach)
        // so the whole app sees one consistent name set. In-memory only.
        const data = raw.attributes ? { ...raw, attributes: normalizeAttributes(raw.attributes) } : raw;
        // Always recompute OVR/xOVR from the (normalized) attributes rather than
        // trusting any stored value — this is the documented design (OVR is never
        // authoritative in Firestore) and it auto-refreshes players whose ratings
        // were saved under the old attribute names before the normalize fix.
        if (data.attributes) {
          try {
            const isPitcherPos = data.primaryPosition === 'P';
            const attrs = isPitcherPos ? data.attributes : { ...data.attributes, pitching: { velocity: 0, control: 0 } };
            const scoutingOVR = calculateOVR(attrs, data.primaryPosition);
            // Pass priorSeasonStats so the xOVR engine can fall back to a
            // discounted baseline when there's no current-season sample yet.
            const priorStats = data.priorSeasonStats?.stats ?? null;
            // Prefer an imported season snapshot (authoritative current-season totals
            // shown on the Stats tab) over the per-game `stats` cache for xOVR.
            const currentStats = data.seasonSnapshot?.stats ?? data.stats ?? null;
            const hasAnyStats = currentStats || priorStats;
            const breakdown = hasAnyStats ? calculateXOVR(scoutingOVR, currentStats, priorStats) : null;
            // Omit the key entirely when there's no breakdown, rather than
            // setting it to undefined — Firestore writes reject undefined
            // outright, and this object gets spread (`...p`) into Firestore
            // payloads elsewhere (e.g. the Game Planner's "Begin Game" write).
            return { id: d.id, ...data, ovr: breakdown ? breakdown.xovr : scoutingOVR, ...(breakdown ? { ovrBreakdown: breakdown } : {}) };
          } catch (e) {
            // Never let one player's data break the whole roster load / lock the app.
            console.error('OVR recompute failed for player', d.id, e);
            return { id: d.id, ...data };
          }
        }
        return { id: d.id, ...data };
      }));
    });

    // includeMetadataChanges only to learn when pending writes upload (End Game
    // writes the game doc and its player docs in one batch, so the game doc's
    // pending state covers both). Metadata-only events update that flag and stop,
    // so the games list re-renders exactly as it did without the option.
    let firstGamesSnap = true;
    const unsubGames = onSnapshot(collection(db, `teams/${team.id}/games`), { includeMetadataChanges: true }, (snap) => {
      setGamesPendingSync(snap.metadata.hasPendingWrites);
      if (!firstGamesSnap && snap.docChanges().length === 0) return;
      firstGamesSnap = false;
      // Practice games are temporary "run the engine" sessions — they must never
      // appear in the Games feed, Home "next game", or stats, regardless of how
      // the coach exits. The active session is tracked via activeGame state, not
      // this list, so filtering here is safe and is the robust fix for orphaned
      // practice docs (cleanup-on-close can be missed; this never is).
      // `source: 'onboardingImport'` docs are a synthesized stats container for
      // a CSV import at team creation, not a game the coach played — hide from
      // the Games feed the same way, but useSeasonStats does its own unfiltered
      // fetch so the imported stats still count toward the season dashboard.
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() })) as any[];
      setGames(all.filter((g: any) => !g.isPractice && g.source !== 'onboardingImport'));
      setOnboardingStatsGame(all.find((g: any) => g.source === 'onboardingImport') ?? null);
    });

    const unsubTournaments = onSnapshot(collection(db, `teams/${team.id}/tournaments`), (snap) => {
      setTournaments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => {
      unsubPlayers();
      unsubGames();
      unsubTournaments();
      setGamesPendingSync(false);
    };
  }, [team?.id]);

  // Reveal the dashboard once the new team has resolved in `teams` and its
  // roster listener has delivered — then clear both the state and the durable
  // localStorage marker. A single hard cap (armed once when the screen appears,
  // NOT reset on every snapshot) guarantees we never get stuck: the pre-seeded
  // data still shows and the live listeners catch up.
  // When the setting-up screen was first shown (for the minimum-dwell floor).
  const settingUpStartedAt = useRef<number | null>(null);
  const finishSettingUp = () => {
    try { localStorage.removeItem('dugout_justOnboarded'); } catch {}
    settingUpStartedAt.current = null;
    // Re-assert BOTH the selected team and the Home tab as we reveal. This is the
    // reliable fix for "landed on Settings showing the OLD team": the reconcile
    // keeps a still-existing prior team selected (so a coach who added a team
    // from another team's Settings stayed on that team's Settings), and the
    // native remount can let a stale localStorage activeTab win. Force both to
    // the just-created team + Home here, after all the churn has settled.
    if (settingUpTeamId) setSelectedTeamId(settingUpTeamId);
    try { localStorage.setItem('dugout_activeTab', 'dashboard'); } catch {}
    setActiveTab('dashboard');
    celebrate(); // reward the wait — a success buzz as the dashboard lands (no-op on web)
    setSettingUpTeamId(null);
  };
  // Minimum dwell so the "setting up" beat feels like real work, not a flash —
  // positive feedback for the effort. Reveal only once BOTH the data is ready
  // AND at least MIN_DWELL_MS has elapsed since the screen appeared.
  const MIN_DWELL_MS = 2500;
  useEffect(() => {
    if (!settingUpTeamId) return;
    if (settingUpStartedAt.current == null) settingUpStartedAt.current = Date.now();
    // Ready = the new team has appeared in the teams list (its doc synced). We do
    // NOT require it to be the *selected* team — the reconcile may keep a prior
    // team selected; finishSettingUp re-selects the new one on reveal. Pre-seeded
    // players mean the roster is already in hand. A stalled sync clears via cap.
    const teamSynced = teams.some(t => t.id === settingUpTeamId);
    if (teamSynced) {
      const elapsed = Date.now() - (settingUpStartedAt.current ?? Date.now());
      const remaining = Math.max(0, MIN_DWELL_MS - elapsed);
      const t = setTimeout(finishSettingUp, remaining);
      return () => clearTimeout(t);
    }
  }, [settingUpTeamId, teams]);
  // Hard cap — armed once per screen appearance (deps: only settingUpTeamId), so
  // rapid `teams` snapshots on native can't keep resetting it.
  useEffect(() => {
    if (!settingUpTeamId) return;
    const cap = setTimeout(finishSettingUp, 8000);
    return () => clearTimeout(cap);
  }, [settingUpTeamId]);

  const handleOpenGame = (game: any) => {
    setActiveTournament(null);
    setActiveLineupGame(game);
  };

  // Post-wizard "setting up" screen. Holds here (spinning trophy + rotating
  // front-office phrases) while the new team + roster sync from Firestore, so
  // the coach never lands on an empty dashboard. Cleared by the settingUpTeamId
  // effect above (with a hard-timeout fallback).
  if (settingUpTeamId) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-canvas px-8 text-center safe-top">
        <Trophy className="w-12 h-12 text-accent-ink animate-spin mb-8" style={{ animationDuration: '1.6s' }} />
        <h1 className="text-2xl font-black italic uppercase tracking-tight text-ink mb-3">
          You're in, Coach
        </h1>
        <RotatingStatus
          className="text-sm font-bold text-ink-2"
          messages={[
            'Setting up your dashboard…',
            'Lining up the front office…',
            'Chalking the baselines…',
            'Filing your roster…',
            'Warming up the bullpen…',
            'Printing the lineup cards…',
            'Rolling out the tarp…',
          ]}
        />
      </div>
    );
  }

  if (showWizard) return (<>
    {/* Escape hatch: a logged-in coach with no team can otherwise only move
        forward through the wizard. The avatar circle (matching the home header)
        opens a small popover with Manage Account (delete-account path, avoiding
        the can't-delete loop) and Sign Out. Replaces the old ACCOUNT/SIGN OUT
        text buttons, which crowded the header and collided with the wizard's
        own back chevron. */}
    <div className="fixed top-0 right-0 z-[60] safe-top p-3">
      <div className="relative">
        <button
          onClick={() => setWizardAccountMenuOpen(v => !v)}
          className={cn(
            "w-10 h-10 rounded-full border-2 transition-all overflow-hidden flex items-center justify-center bg-canvas shadow-lg",
            wizardAccountMenuOpen ? "border-ink scale-105" : "border-hairline hover:border-ink-mute"
          )}
          aria-label="Account menu"
        >
          {(coachData?.photoURL || user?.photoURL) ? (
            <img src={coachData?.photoURL || user?.photoURL} referrerPolicy="no-referrer" alt="Account" className="w-full h-full object-cover" />
          ) : (
            <UserIcon className="w-5 h-5 text-ink-2" />
          )}
        </button>
        {wizardAccountMenuOpen && (
          <>
            <div className="fixed inset-0 z-[59]" onClick={() => setWizardAccountMenuOpen(false)} />
            <div className="absolute top-full right-0 mt-2 z-[61] w-44 bg-surface border border-hairline rounded-2xl shadow-2xl overflow-hidden">
              <button
                onClick={() => { setWizardAccountMenuOpen(false); setShowWizardAccount(true); }}
                className="w-full text-left px-4 py-3 text-[11px] font-black uppercase tracking-widest text-ink-2 hover:bg-canvas hover:text-ink transition-colors"
              >
                Manage Account
              </button>
              <div className="border-t border-hairline" />
              <button
                onClick={() => { void clearPushRegistration(); signOut(auth); }}
                className="w-full text-left px-4 py-3 text-[11px] font-black uppercase tracking-widest text-ink-2 hover:bg-canvas hover:text-ink transition-colors"
              >
                Sign Out
              </button>
            </div>
          </>
        )}
      </div>
    </div>
    {showWizardAccount && (
      <ManageAccountModal user={user} teams={teams} isElite={effectiveIsElite} eliteSources={eliteSources} grantExpiresAt={grantExpiresAt} onClose={() => setShowWizardAccount(false)} onUpgrade={() => setEliteModal('upgrade')} />
    )}
    {/* The wizard branch returns early, so the dashboard's EliteUpgradeModal
        instance below never renders here — it needs its own. Without this,
        Upgrade to Elite from the wizard's Account modal silently no-ops
        (App Store rejection 2026-07-06: "UPGRADE TO ELITE button was unresponsive"). */}
    {eliteModal && user && (
      <EliteUpgradeModal
        uid={user.uid}
        mode={eliteModal}
        isElite={effectiveIsElite}
        onDismiss={() => setEliteModal(null)}
      />
    )}
    <OnboardingWizard user={user} onCancel={() => {
      // Cancelling the wizard is only safe when the coach still has a team to
      // return to. With no team (e.g. they reached the wizard by deleting their
      // last team), dismissing it drops to the `!team` branch = an infinite
      // spinner with no way back, since the reconcile that showed the wizard
      // won't re-fire without a new snapshot. So keep them in the wizard.
      if (teams.length > 0) setShowWizard(false);
    }} onComplete={(newTeamId, prepopulated, options) => {
    // IMPORTANT: on native the auth user object can re-fire during onboarding,
    // which remounts <Dashboard> (App.tsx renders `user ? <Dashboard/> : ...`)
    // and wipes all transient state — the pre-seeded roster, the active tab, and
    // the setting-up flag. So we persist the just-onboarded intent to localStorage
    // and read it back on mount, instead of trusting in-memory state to survive.
    try {
      localStorage.setItem('dugout_activeTab', 'dashboard');       // land on Home, remount-proof
      localStorage.setItem('dugout_justOnboarded', newTeamId);     // show setting-up screen on mount
    } catch {}
    setShowWizard(false);
    setSelectedTeamId(newTeamId);
    setActiveTab('dashboard');
    // Seed games AND players state immediately so the coach doesn't land on
    // an empty home tab while the Firestore snapshot listeners catch up. The
    // listeners will overwrite these with the canonical snapshots moments later.
    if (prepopulated?.games && prepopulated.games.length > 0) {
      setGames(prepopulated.games);
    }
    if (prepopulated?.players && prepopulated.players.length > 0) {
      setPlayers(prepopulated.players);
    }
    // Arrival screen's "Scout Your Roster" CTA — open the scouting session
    // right on top of Home.
    if (options?.openScouting) {
      setShowScoutingSession(true);
    }
    setSettingUpTeamId(newTeamId);
  }} />
  </>);

  // No team selected. The reconcile effect routes a teamless coach to the
  // wizard (handled above via showWizard); this is the brief gap before that
  // state settles. Render a neutral loading state, never a blank white page.
  if (!team) return <TeamLoadingScreen />;

  // Elite League white design system is now app-wide
  const homeLight = true;

  return (
    <div className="h-screen bg-surface text-ink flex flex-col print:min-h-0 print:block overflow-hidden safe-x">
      {/* Top Bar */}
      <header
        className="p-6 flex items-center justify-between backdrop-blur-xl sticky top-0 z-20 print:hidden"
        style={{
          // Safe-area inset (iOS notch) PLUS a fixed cushion so the title has
          // breathing room on web/desktop too, where the inset is 0.
          paddingTop: 'calc(env(safe-area-inset-top) + 18px)',
          // Match the slate canvas (#F7F8F9) and drop the bottom border so the
          // header blends into the page — no interrupting horizontal line.
          backgroundColor: '#F7F8F9',
        }}
      >
        <div className="flex items-center space-x-3">
          <div
            className="w-10 h-10 bg-ink rounded-lg flex items-center justify-center overflow-hidden"
            style={homeLight ? { backgroundColor: team.logoURL ? 'transparent' : '#141A21' } : undefined}
          >
            {team.logoURL ? (
              <img src={team.logoURL} alt="Team Logo" className="w-full h-full object-cover" />
            ) : (
              <img src="/dugout-mark.png" alt="Dugout" className="w-full h-full object-cover" />
            )}
          </div>
          <div>
            <h1 className="text-lg font-black italic tracking-tight uppercase" style={homeLight ? { color: '#141A21' } : undefined}>{team.name}</h1>
            <div className="flex items-center space-x-2">
              <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest" style={homeLight ? { color: '#5A6672' } : undefined}>{team.ageGroup} • {team.level}</p>
              {team.settings?.showRecordOnDashboard !== false && (
                <>
                  <span className="text-ink-2" style={homeLight ? { color: '#E3E7EB' } : undefined}>•</span>
                  <p className="text-[10px] font-semibold text-accent-ink uppercase tracking-widest" style={homeLight ? { color: '#141A21' } : undefined}>{recordString}</p>
                </>
              )}
            </div>
          </div>
          {players.length > 0 && team.settings?.showTeamOvrInHeader !== false && (
            <div className="flex flex-col items-center ml-1">
              <div
                className="w-11 h-11 bg-ink rounded-2xl flex items-center justify-center shadow-[0_0_20px_rgba(16,185,129,0.3)]"
                style={homeLight ? { backgroundColor: '#141A21', boxShadow: 'none' } : undefined}
              >
                <span className="text-surface font-black text-lg italic leading-none" style={homeLight ? { color: '#FFFFFF' } : undefined}>
                  {Math.round(players.reduce((acc, p) => acc + (p.ovr || 0), 0) / players.length)}
                </span>
              </div>
              <span className="text-[8px] font-semibold text-ink-2 uppercase tracking-widest mt-0.5" style={homeLight ? { color: '#5A6672' } : undefined}>
                {players.some(p => p.ovrBreakdown?.confidenceLevel !== 'none') ? 'xOVR' : 'OVR'}
              </span>
            </div>
          )}
        </div>
        <UserAccountMenu user={user} coachData={coachData} team={team} teams={teams} selectedTeamId={selectedTeamId} isHeadCoach={isHeadCoach} isElite={effectiveIsElite} eliteSources={eliteSources} grantExpiresAt={grantExpiresAt} onSelectTeam={setSelectedTeamId} onAddTeam={() => setShowWizard(true)} onNavigate={(tab) => setActiveTab(tab)} onUpgrade={() => setEliteModal('upgrade')} />
      </header>

      {/* Demo session banner.
          Hidden for marketing screen-recording via ?hideDemoBanner=1 — keeps the
          banner on for real visitors while letting a recording show a clean app. */}
      {team.isDemoSession && team.expiresAt &&
        !new URLSearchParams(window.location.search).has('hideDemoBanner') && (
        <div className="bg-gold-soft border-b border-hairline px-4 py-2.5 flex items-center justify-between gap-3 print:hidden">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-gold-ink text-xs">⚾</span>
            <p className="text-xs font-semibold text-gold-ink uppercase tracking-widest shrink-0">Demo Mode</p>
            <p className="text-[10px] font-bold text-gold-ink/70 truncate">
              · Resets in {getDemoTimeRemaining(team.expiresAt)} · All changes are temporary
            </p>
          </div>
          <button
            onClick={() => signOut(auth)}
            className="text-[10px] font-black uppercase tracking-widest text-surface bg-amber-400 hover:bg-amber-300 transition-colors px-3 py-1.5 rounded-lg shrink-0"
          >
            Create Your Account
          </button>
        </div>
      )}

      {/* Live game resume banner — visible on all tabs when a game is in progress */}
      {(() => {
        const liveGame = games.find(isGameLive);
        if (!liveGame) return null;
        return (
          <button
            onClick={() => setActiveGame(liveGame)}
            className="w-full flex items-center justify-between px-5 py-3 bg-ink text-surface print:hidden"
          >
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest animate-pulse">● LIVE</span>
              <span className="text-sm font-black italic uppercase">
                {liveGame.isHome ? 'vs' : '@'} {liveGame.opponent}
              </span>
              {liveGame.currentInning && (
                <span className="text-[10px] font-bold">· Inning {liveGame.currentInning}</span>
              )}
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-widest">Resume →</span>
          </button>
        );
      })()}

      <SyncNote pending={gamesPendingSync} />

      <main ref={gamesScrollContainerRef} className="flex-1 overflow-y-auto pb-24 print:hidden" style={homeLight ? { backgroundColor: '#F7F8F9' } : undefined}>
        <AnimatePresence mode="wait">
          {activeTab === 'dashboard' && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-canvas"
              style={{ minHeight: '100%' }}
            >
              {showSignupOverlap && (
                <div className="mx-4 mt-4 rounded-card border border-hairline bg-raised p-4 space-y-3">
                  <p className="text-sm font-semibold text-ink">
                    {recordOverlaps && statsOverlap ? 'Your record and stats may be counted twice'
                      : recordOverlaps ? 'Your record may be counted twice'
                      : 'Your player stats may be counted twice'}
                  </p>
                  <p className="text-xs text-ink-2 leading-relaxed">
                    {recordOverlaps && <>You entered a starting record of {startW}-{startL}{startT > 0 ? `-${startT}` : ''} at signup. </>}
                    {statsOverlap && <>You imported season stats at signup. </>}
                    You've since logged the games from before you joined, so they're counted twice. Clear the signup {recordOverlaps && statsOverlap ? 'totals' : recordOverlaps ? 'record' : 'stats'} and each game will count once.
                  </p>
                  <div className="flex gap-2">
                    <button disabled={clearingSignup} onClick={() => resolveSignupOverlap(true)} className="px-3 py-2 rounded-control bg-accent text-ink text-xs font-semibold disabled:opacity-50">{clearingSignup ? 'Clearing...' : 'Clear signup totals'}</button>
                    <button disabled={clearingSignup} onClick={() => resolveSignupOverlap(false)} className="px-3 py-2 rounded-control border border-hairline text-ink-2 text-xs font-semibold disabled:opacity-50">Keep them</button>
                  </div>
                </div>
              )}
              <HomeTab
                team={team}
                games={games}
                players={players}
                tournaments={tournaments}
                recordString={recordString}
                teamLeaders={teamLeaders}
                hotPlayers={hotPlayers}
                playerSeasonStats={playerSeasonStats}
                completedGames={completedGames}
                isHeadCoach={isHeadCoach}
                effectiveIsElite={effectiveIsElite}
                welcomeInitialUnseen={welcomeInitialUnseen}
                welcomeDismissed={welcomeDismissed}
                setWelcomeDismissed={setWelcomeDismissed}
                onPlanGame={openPlanGame}
                onLive={(g: any) => { if (isGameLive(g)) { tapMedium(); setActiveGame(g); } else setActiveLineupGame(g); }}
                onOpenTab={(tab: string) => setActiveTab(tab)}
                onAddGame={() => setIsAddingGame(true)}
                onCreateTournament={() => effectiveIsElite ? setIsCreatingTournament(true) : setEliteModal('upgrade')}
                onScoutRoster={() => setShowScoutingSession(true)}
                onCalendarSync={() => setShowChecklistCalendarSync(true)}
                onStatsImport={() => setShowSeasonStatsImport(true)}
                onUploadGameStats={(g: any) => openGameImport(g)}
                onUpgrade={() => setEliteModal('upgrade')}
                onOpenTournament={(tournamentId: string) => {
                  const t = tournaments.find((t: any) => t.id === tournamentId);
                  if (t) setActiveTournament(t);
                }}
                onSmartScout={() => { setActiveTab('stats'); setAutoOpenSmartScout(true); }}
                devReviewCount={devReviewProposals.length}
                onOpenDevReview={() => setShowDevReview(true)}
              />
            </motion.div>
          )}
          {activeTab === 'roster' && (
            <motion.div
              key="roster"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-canvas"
              style={{ minHeight: '100%' }}
            >
              <RosterTab
                players={players}
                team={team}
                user={user}
                isHeadCoach={isHeadCoach}
                isAssistantCoach={isAssistantCoach}
                effectiveIsElite={effectiveIsElite}
                devReviewProposals={devReviewProposals}
                showRetired={showRetired}
                setShowRetired={setShowRetired}
                playerSeasonStats={playerSeasonStats}
                playerLastGame={playerLastGame}
                playerHotCold={playerHotCold}
                setIsAddingGuestPlayer={setIsAddingGuestPlayer}
                setIsAddingPlayer={setIsAddingPlayer}
                setShowDevReview={setShowDevReview}
                setEditingPlayer={(p: any) => { setPlayerCardTab('stats'); setEditingPlayer(p); }}
                setScoutingPlayer={setScoutingPlayer}
                setEliteModal={setEliteModal}
              />
            </motion.div>
          )}

          {activeTab === 'games' && (
            <motion.div
              key="games"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-canvas"
              style={{ minHeight: '100%' }}
            >
              <GamesTab
                games={seasonGames}
                tournaments={tournaments}
                team={team}
                completedGames={completedGames}
                hasArchivedSeasons={hasArchivedSeasons}
                onOpenSeasonHistory={() => setShowSeasonHistory(true)}
                isHeadCoach={isHeadCoach}
                effectiveIsElite={effectiveIsElite}
                importMenuOpen={importMenuOpen}
                addMenuOpen={addMenuOpen}
                setImportMenuOpen={setImportMenuOpen}
                setAddMenuOpen={setAddMenuOpen}
                nextGameDividerRef={nextGameDividerRef}
                deleteGame={deleteGame}
                openGameImport={openGameImport}
                openGameData={openGameData}
                openGameInsights={setInsightsGame}
                gameHasInsights={gameHasInsights}
                setEliteModal={setEliteModal}
                setIsImportingCalendar={setIsImportingCalendar}
                setIsAddingGame={setIsAddingGame}
                setIsCreatingTournament={setIsCreatingTournament}
                setActiveLineupGame={setActiveLineupGame}
                setActiveGame={setActiveGame}
                setActivePlanGame={setActivePlanGame}
                setEditingGame={setEditingGame}
                setEditGamePanelGame={setEditGamePanelGame}
                setActiveTournament={setActiveTournament}
                setEnterResultsGame={setEnterResultsGame}
              />
            </motion.div>
          )}
          {activeTab === 'stats' && (
            <motion.div 
              key="stats"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <StatsView team={team} players={players.filter((p: any) => !p.isGuest && !p.retired)} isElite={effectiveIsElite} isHeadCoach={isHeadCoach} onUpgrade={() => setEliteModal('upgrade')} autoOpenSmartScout={autoOpenSmartScout} onSmartScoutAutoOpened={() => setAutoOpenSmartScout(false)} />
            </motion.div>
          )}
          {activeTab === 'settings' && (
            <motion.div 
              key="settings"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <SettingsView team={team} players={players} seasonGames={seasonGames} onboardingStatsGame={onboardingStatsGame} isHeadCoach={isHeadCoach} userEmail={user?.email ?? ''} uid={user?.uid ?? ''} isAdmin={user?.email === 'djacobsen219@gmail.com'} onOpenSeasonHistory={() => setShowSeasonHistory(true)} onOpenWrapped={() => setShowWrapped(true)} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Bottom Nav */}
      <nav
        className="fixed bottom-0 left-0 right-0 pt-4 px-4 pb-4 safe-bottom bg-surface backdrop-blur-2xl border-t border-hairline z-30 print:hidden"
        style={homeLight ? { backgroundColor: '#FFFFFF', borderTop: '0.5px solid #E3E7EB' } : undefined}
      >
        <div className="max-w-md mx-auto flex items-center justify-around">
          {([
            { id: 'dashboard', label: 'Home' },
            { id: 'roster',    label: 'Roster' },
            { id: 'games',     label: 'Games' },
            { id: 'stats',     label: 'Stats' },
            { id: 'settings',  label: 'Settings' },
          ] as const).map(item => {
            const isActive = activeTab === item.id;
            const color = isActive ? '#141A21' : '#9AA5B1';
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', background: 'none', border: 'none', cursor: 'pointer', color, padding: '0 4px' }}
              >
                {item.id === 'dashboard' && <NavHomePlate active={isActive} className="w-6 h-6" />}
                {item.id === 'roster'    && <NavRoster    active={isActive} className="w-6 h-6" />}
                {item.id === 'games'     && <NavCalendar  active={isActive} className="w-6 h-6" />}
                {item.id === 'stats'     && <NavStats     active={isActive} className="w-6 h-6" />}
                {item.id === 'settings'  && <NavSettings  active={isActive} className="w-6 h-6" />}
                <span style={{ fontSize: '10px', fontWeight: isActive ? 700 : 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <AnimatePresence>
        {scoutingPlayer && (
          <ScoutingWizard
            player={scoutingPlayer}
            team={team}
            currentUserId={user?.uid ?? ''}
            onClose={() => setScoutingPlayer(null)}
          />
        )}
        {isImportingCalendar && (
          <CalendarImporter
            teamId={team.id}
            teamName={team.name}
            onClose={() => setIsImportingCalendar(false)}
          />
        )}
        {/* Onboarding: Roster Scouting Session */}
        {showScoutingSession && isHeadCoach && (
          <RosterScoutingSession
            teamId={team.id}
            players={players}
            isElite={effectiveIsElite}
            onClose={() => setShowScoutingSession(false)}
            onComplete={() => setShowScoutingSession(false)}
            onRequestEliteTrial={(ranked) => {
              setShowScoutingSession(false);
              setScoutingEliteRanked(ranked);
            }}
          />
        )}
        {/* Onboarding: Elite trial prompt shown after scouting reveal for non-Elite coaches */}
        {scoutingEliteRanked && !effectiveIsElite && (
          <ScoutingElitePrompt
            rankedPlayers={scoutingEliteRanked}
            onStartTrial={() => {
              setScoutingEliteRanked(null);
              setEliteModal('upgrade');
            }}
            onSkip={() => setScoutingEliteRanked(null)}
          />
        )}
        {/* Onboarding: Calendar sync bottom sheet */}
        {showChecklistCalendarSync && (
          <AnimatePresence>
            <ChecklistCalendarSync
              teamId={team.id}
              teamName={team.name}
              onClose={() => setShowChecklistCalendarSync(false)}
              onSuccess={(count) => {
                setShowChecklistCalendarSync(false);
                setCalendarSyncSuccessCount(count);
              }}
            />
          </AnimatePresence>
        )}
        {/* Onboarding: Calendar sync success toast */}
        {calendarSyncSuccessCount !== null && (
          <div className="fixed bottom-28 left-1/2 -translate-x-1/2 z-50 bg-ink text-surface font-black text-sm px-5 py-3 rounded-2xl shadow-lg whitespace-nowrap animate-fade-in">
            {calendarSyncSuccessCount > 0 ? `${calendarSyncSuccessCount} games added to your schedule` : 'Schedule already up to date'}
            <button
              onClick={() => setCalendarSyncSuccessCount(null)}
              className="ml-3 opacity-60 hover:opacity-100"
            >✕</button>
          </div>
        )}

        {gcImport && (
          <GameDataImport
            key={gcImport.game?.id || 'any'}
            team={team}
            players={players.filter((p: any) => !p.isGuest && !p.retired)}
            games={games}
            game={gcImport.game ? (games.find(g => g.id === gcImport.game.id) ?? gcImport.game) : undefined}
            isElite={effectiveIsElite}
            eliteLoading={isHeadCoach && !team.isDemoSession && subLoading}
            onUpgrade={() => setEliteModal('upgrade')}
            onViewBoxScore={(g: any) => { closeGameImport(); setBoxScoreGame(g); }}
            incoming={gcIncoming}
            onClose={closeGameImport}
          />
        )}
        {insightsGame && (
          <GameInsightsSheet game={insightsGame} teamId={team.id} onClose={() => setInsightsGame(null)} />
        )}
        {boxScoreGame && (
          <StatsImportModal
            isOpen
            initialStep="boxscore"
            game={games.find(g => g.id === boxScoreGame.id) ?? boxScoreGame}
            team={team}
            teamId={team.id}
            players={players}
            onClose={() => setBoxScoreGame(null)}
            onUpdateStats={() => openGameImport(games.find(g => g.id === boxScoreGame.id) ?? boxScoreGame)}
          />
        )}
        {/* Onboarding: Season-level stats import (no game context). The
            checklist step is labeled "Upload prior season stats" so we route
            to the priorSeasonStats baseline path — these stats won't mix with
            the current season's live tracking. */}
        {showSeasonStatsImport && (
          <StatsImportModal
            isOpen={showSeasonStatsImport}
            onClose={() => {
              setShowSeasonStatsImport(false);
              updateDoc(doc(db, 'teams', team.id), {
                'settings.onboardingChecklist.statsUploaded': true,
              });
            }}
            teamId={team.id}
            team={team}
            players={players}
            mode="priorBaseline"
          />
        )}
        {showSeasonHistory && (
          <SeasonHistoryModal
            team={team}
            players={players}
            isElite={effectiveIsElite}
            onClose={() => setShowSeasonHistory(false)}
          />
        )}
        {showWrapped && (
          <SeasonWrappedModal
            team={team}
            players={players}
            games={games}
            onClose={() => setShowWrapped(false)}
          />
        )}
        {editGamePanelGame && (
          <EditGamePanel
            game={editGamePanelGame}
            team={team}
            teamId={team.id}
            players={players}
            onClose={() => setEditGamePanelGame(null)}
            onOpenPositions={(g) => {
              setEditGamePanelGame(null);
              if (g.status === 'completed') setPositionsEditorGame(g);
              else setActivePlanGame(g);
            }}
            onOpenStats={(g) => { setEditGamePanelGame(null); openGameImport(g); }}
          />
        )}
        {(isAddingGame || editingGame) && (
          <GameForm
            teamId={team.id}
            team={team}
            game={editingGame}
            onGameSaved={(g) => {
              // Merge by id: replaces the matching game on edit, adds it on
              // create. The games snapshot (also keyed by doc id) reconciles to
              // the same entry moments later, so this can never duplicate — it
              // just removes the native snapshot-lag gap.
              if (g.isPractice) return;
              setGames(prev => {
                const rest = prev.filter(x => x.id !== g.id);
                return [...rest, g];
              });
            }}
            onClose={() => {
              setIsAddingGame(false);
              setEditingGame(null);
            }}
          />
        )}
        {activeLineupGame && (
          <LineupBuilder
            game={activeLineupGame}
            team={team}
            teamId={team.id}
            players={gameDayPlayers}
            games={games}
            tournaments={tournaments}
            playerSeasonStats={playerSeasonStats}
            playerHotCold={playerHotCold}
            onClose={() => {
              // Backing out of the lineup builder for a practice game must delete
              // the temp doc, or it orphans a ghost game in Firestore. Not awaited:
              // with no signal the delete only settles once it uploads.
              if (activeLineupGame.isPractice) {
                deleteDoc(doc(db, `teams/${team.id}/games`, activeLineupGame.id)).catch(() => {});
              }
              setActiveLineupGame(null);
            }}
            onPlayBall={(game: any) => {
              tapMedium(); // Play Ball — the game is starting
              setActiveLineupGame(null);
              setActiveGame(game);
            }}
          />
        )}
        {positionsEditorGame && (
          <GamePositionsEditor
            game={positionsEditorGame}
            team={team}
            players={players}
            gameDayPlayers={gameDayPlayers}
            onClose={() => setPositionsEditorGame(null)}
          />
        )}
        {activePlanGame && (
          <PlanGameView
            game={activePlanGame}
            team={team}
            players={gameDayPlayers}
            playerSeasonStats={playerSeasonStats}
            playerFieldTimeStats={playerFieldTimeStats}
            isElite={effectiveIsElite}
            onUpgrade={() => setEliteModal('upgrade')}
            initialTab={planGameInitialTab}
            onClose={() => { setActivePlanGame(null); setPlanGameInitialTab('gameplan'); }}
            onPlayerTap={(player: any) => { if (isHeadCoach) setEditingPlayer(player); }}
            // The wizard's review card offers "Begin Game", which hands straight
            // to the live engine. Same transition as the Lineup Builder's Play
            // Ball, so a coach can plan and start without backing out first.
            onBeginGame={(g: any) => {
              tapMedium();
              setActivePlanGame(null);
              setPlanGameInitialTab('gameplan');
              setActiveGame(g);
            }}
            // `firstGamePlanned` is no longer marked here — it's now derived
            // from the games snapshot (see useEffect at the top of Dashboard)
            // so we only mark complete when a plan actually persists.
          />
        )}
        {activeGame && (
          <LiveGameEngine
            game={activeGame}
            team={team}
            players={gameDayPlayers}
            teamId={team.id}
            games={games}
            tournaments={tournaments}
            onClose={() => {
              if (activeGame.isPractice) {
                // Clean up the temporary practice game (not awaited, see above)
                deleteDoc(doc(db, `teams/${team.id}/games`, activeGame.id)).catch(() => {});
              }
              setActiveGame(null);
            }}
            hasUnsyncedChanges={gamesPendingSync}
            isHeadCoach={isHeadCoach}
            isElite={effectiveIsElite}
            onUpgrade={() => setEliteModal('upgrade')}
            isPracticeMode={!!activeGame.isPractice}
          />
        )}
        {isCreatingTournament && (
          <TournamentForm
            teamId={team.id}
            teamSettings={team.settings}
            onClose={() => setIsCreatingTournament(false)}
            onCreated={(id) => {
              setIsCreatingTournament(false);
              const newT = tournaments.find(t => t.id === id);
              if (newT) setActiveTournament(newT);
            }}
          />
        )}
        {editingTournament && (
          <TournamentForm
            teamId={team.id}
            teamSettings={team.settings}
            tournament={editingTournament}
            onClose={() => setEditingTournament(null)}
            onCreated={(id) => {
              setEditingTournament(null);
              const updatedT = tournaments.find(t => t.id === id);
              if (updatedT) setActiveTournament(updatedT);
            }}
          />
        )}
        {activeTournament && (
          <TournamentView
            tournament={activeTournament}
            team={team}
            teamId={team.id}
            players={gameDayPlayers}
            allGames={games}
            onClose={() => setActiveTournament(null)}
            onOpenGame={handleOpenGame}
            onEdit={() => {
              setEditingTournament(activeTournament);
              setActiveTournament(null);
            }}
            onImportStats={(g: any) => openGameImport(g)}
            onEnterResults={setEnterResultsGame}
            // Plan a tournament game without leaving the tournament. PlanGameView
            // is z-[100] and TournamentView is z-50, so the planner layers on top
            // and closing drops the coach back on the tournament dashboard.
            onPlanGame={(g: any) => openPlanGame(g, 'gameplan')}
          />
        )}
        {enterResultsGame && (
          <EnterResultsModal
            isOpen={!!enterResultsGame}
            onClose={() => setEnterResultsGame(null)}
            game={enterResultsGame}
            team={team}
            teamId={team.id}
            onSuccess={(gameId) => {
              const game = games.find(g => g.id === gameId) ?? enterResultsGame;
              setEnterResultsGame(null);
              openGameImport(game);
            }}
          />
        )}
        {(isAddingPlayer || isAddingGuestPlayer || editingPlayer) && (() => {
          // When editing an existing player, wire up swipe-to-next/prev across the
          // active roster (same order as the Roster tab). Remounting via key on the
          // player id re-seeds PlayerForm's internal state cleanly for each player.
          const rosterIdx = editingPlayer ? orderedRosterPlayers.findIndex((p: any) => p.id === editingPlayer.id) : -1;
          const goTo = (delta: number) => {
            if (rosterIdx < 0) return;
            const next = orderedRosterPlayers[rosterIdx + delta];
            if (next) setEditingPlayer(next); // playerCardTab is preserved across swipes
          };
          return (
          <PlayerForm
            key={editingPlayer?.id ?? 'new-player'}
            teamId={team.id}
            team={team}
            player={editingPlayer}
            defaultIsGuest={isAddingGuestPlayer && !editingPlayer}
            includeAssistantScouting={team?.settings?.includeAssistantScouting ?? false}
            initialTab={editingPlayer ? playerCardTab : undefined}
            onTabChange={setPlayerCardTab}
            hasPrevPlayer={rosterIdx > 0}
            hasNextPlayer={rosterIdx >= 0 && rosterIdx < orderedRosterPlayers.length - 1}
            onPrevPlayer={() => goTo(-1)}
            onNextPlayer={() => goTo(1)}
            onGoToImport={() => {
              setIsAddingPlayer(false);
              setIsAddingGuestPlayer(false);
              setEditingPlayer(null);
              setActiveTab('games');
            }}
            onClose={() => {
              setIsAddingPlayer(false);
              setIsAddingGuestPlayer(false);
              setEditingPlayer(null);
            }}
          />
          );
        })()}
      </AnimatePresence>

      {eliteModal && user && (
        <EliteUpgradeModal
          uid={user.uid}
          mode={eliteModal}
          isElite={effectiveIsElite}
          onDismiss={() => setEliteModal(null)}
        />
      )}

      {showDevReview && devReviewProposals.length > 0 && (
        <PlayerDevReviewModal
          proposals={devReviewProposals}
          onAccept={async (proposal) => {
            const playerRef = doc(db, `teams/${team.id}/players/${proposal.playerId}`);
            const player = players.find((p: any) => p.id === proposal.playerId);
            if (!player?.attributes) return;
            const updatedAttributes = {
              ...player.attributes,
              [proposal.attributeBucket]: {
                ...player.attributes[proposal.attributeBucket],
                [proposal.attributeKey]: proposal.proposedValue,
              },
            };
            await updateDoc(playerRef, {
              [`attributes.${proposal.attributeBucket}.${proposal.attributeKey}`]: proposal.proposedValue,
              devReviewedAt: new Date().toISOString(),
            });
          }}
          onSkip={(proposal) => {
            const playerRef = doc(db, `teams/${team.id}/players/${proposal.playerId}`);
            updateDoc(playerRef, { devReviewDismissedAt: new Date().toISOString() });
          }}
          onClose={() => setShowDevReview(false)}
        />
      )}
    </div>
  );
}

export default function App() {
  const [demoLoading, setDemoLoading] = useState(false);

  // The app no longer applies a global theme class. Colors come from the
  // semantic tokens in src/index.css; the legacy palette inversion is scoped to
  // the few screens still authored against it via `legacy-invert` on their own
  // roots. The old `dugout_theme` localStorage key is unused and intentionally
  // not read — it was per-device, so it leaked across accounts on a shared
  // device.

  return (
    <ErrorBoundary>
      <Toaster />
      <AuthProvider>
        <AuthContext.Consumer>
          {({ user, loading }) => {
            if (loading || demoLoading) return (
              <div className="min-h-screen bg-surface flex flex-col items-center justify-center gap-3">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <Trophy className="w-8 h-8 text-accent-ink" />
                </motion.div>
                {demoLoading && (
                  <p className="text-[11px] font-semibold text-ink-2 uppercase tracking-widest">Setting up your demo...</p>
                )}
              </div>
            );
            return user ? <Dashboard /> : <Login onDemoLoadingChange={setDemoLoading} />;
          }}
        </AuthContext.Consumer>
      </AuthProvider>
    </ErrorBoundary>
  );
}